import { useState, useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import {
  Navigation, LocateFixed, Phone, AlertTriangle, Settings, FileSpreadsheet,
  Check, X, RefreshCw, Volume2, VolumeX, Hand, TrainFront, Shield, Activity, Download, Search, FileText, Upload, Loader2, Users, CalendarDays
} from 'lucide-react';
import L from 'leaflet';
import { Capacitor } from '@capacitor/core';
import 'leaflet/dist/leaflet.css';
import PKMarkers from './components/PKMarkers';
import SlowDownManager from './components/SlowDownManager';
import MissionManager from './components/MissionManager';
import ConductorLeavesManager from './components/ConductorLeavesManager';
import LTVMapLayer from './components/LTVMapLayer';
import StationsMapLayer from './components/StationsMapLayer';
import IncidentsLayer from './components/IncidentsLayer';
import IncidentManager from './components/IncidentManager';
import IncidentValidator from './components/IncidentValidator';
import SecurityRegisterView from './components/SecurityRegisterView';
import SecuDashboard from './components/SecuDashboard';
import PdfReaderView from './components/PdfReaderView';
import BulletinManager from './components/BulletinManager';
import AdminUserManagement from './components/AdminUserManagement';
import StationMasterView from './components/StationMasterView';
import TractionOperatorView from './components/TractionOperatorView';
import { useTrainTelemetry } from './hooks/useTrainTelemetry';
import { Geolocation } from '@capacitor/geolocation';
import { LocalNotifications } from '@capacitor/local-notifications';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { PERMANENT_MISSIONS, Mission } from './data/missions';
import { STATIONS } from './data/stations';
import { TrackProvider, useTrack } from './context/TrackContext';
import { getPointAtPK } from './utils/geometry';
import { supabase } from './utils/supabaseClient';
import { localApi, API_BASE_URL } from './utils/localApi';
import { loadToken } from './utils/tokenStorage';

import Login from './components/Login';
import EvsView from './components/EvsView';
import ConductorView from './components/ConductorView';
import RequestedStopsView from './components/RequestedStopsView';
import TrainPositionsLayer from './components/TrainPositionsLayer';
import initWasm, { get_rust_greeting, calculate_braking_distance } from './wasm/nexus_core';
import wasmUrl from './wasm/nexus_core_bg.wasm?url';

// --- CUSTOM PREMIUM ICONS ---
const PowerIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
    <line x1="12" y1="2" x2="12" y2="12" />
  </svg>
);


// Fix icônes Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function MapResizer() {
  const map = useMap();
  useEffect(() => {
    // Plusieurs invalidations pour garantir le bon calcul de la taille du conteneur après le chargement du DOM
    const timer1 = setTimeout(() => { map.invalidateSize(); }, 150);
    const timer2 = setTimeout(() => { map.invalidateSize(); }, 600);
    const timer3 = setTimeout(() => { map.invalidateSize(); }, 1500);
    
    const handleResize = () => {
      map.invalidateSize();
    };
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      window.removeEventListener('resize', handleResize);
    };
  }, [map]);
  return null;
}
function RecenterMapHandler({ currentCoords, isGpsTracking, setIsGpsTracking, recenterTrigger }: { currentCoords: [number, number] | null, isGpsTracking: boolean, setIsGpsTracking: (v: boolean) => void, recenterTrigger: number }) {
  const map = useMap();

  useEffect(() => {
    if (isGpsTracking && currentCoords) {
      map.setZoom(16);
      map.panTo(currentCoords, { animate: true, duration: 2 });
    }
  }, [currentCoords, isGpsTracking, map]);

  useEffect(() => {
    if (recenterTrigger > 0 && currentCoords) {
      map.flyTo(currentCoords, 16, { animate: true, duration: 1 });
    }
  }, [recenterTrigger, currentCoords, map]);

  useEffect(() => {
    const handleDragStart = () => setIsGpsTracking(false);
    map.on('dragstart', handleDragStart);
    return () => { map.off('dragstart', handleDragStart); };
  }, [map, setIsGpsTracking]);

  return null;
}

function AutoCenterOnMissionStart({ activeMission }: { activeMission: Mission | null }) {
  const map = useMap();
  const { paths } = useTrack();

  useEffect(() => {
    if (activeMission && activeMission.stops && activeMission.stops.length > 0) {
      const firstStop = activeMission.stops[0];
      let leafletCoords: [number, number] | null = null;

      // 1. Chercher d'abord la station exacte dans notre base de données pour avoir des coordonnées 100% précises
      const matchedStation = STATIONS.find(s => 
        s.name.toLowerCase() === firstStop.stationName.toLowerCase() ||
        Math.abs(s.pk - firstStop.pk) < 0.05
      );

      if (matchedStation && matchedStation.coordinates) {
        leafletCoords = [matchedStation.coordinates[1], matchedStation.coordinates[0]];
      } else if (paths.balagne.length > 0 || paths.centrale.length > 0) {
        // 2. Fallback par interpolation PK géométrique si pas de coordonnées explicites
        let coords = getPointAtPK(paths.balagne, firstStop.pk, 119.9, 46.7);
        if (!coords) coords = getPointAtPK(paths.centrale, firstStop.pk, 0, 157.5);
        if (coords) {
          leafletCoords = [coords[1], coords[0]];
        }
      }

      if (leafletCoords) {
        // Un délai minuscule pour laisser le temps au container de s'afficher d'abord
        setTimeout(() => {
          map.flyTo(leafletCoords!, 16, { animate: true, duration: 1.5 });
        }, 150);
      }
    }
  }, [activeMission, map, paths]);

  return null;
}

function MissionSelection({
  missions,
  onSelect
}: {
  missions: Mission[],
  onSelect: (m: Mission) => void
}) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredMissions = useMemo(() => {
    return missions.filter(m => 
      m.trainNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.lineName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [missions, searchQuery]);

  return (
    <div className="flex-1 flex flex-col bg-[#020617] p-2 sm:p-4 z-[2000] absolute inset-0 overflow-y-auto w-full h-full">
      <div className="max-w-6xl w-full m-auto py-4 sm:py-8">
        <div className="text-center mb-6">
          <img src="logo.png" alt="ASTA RAIL" className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 object-contain" />
          <h1 className="text-xl sm:text-3xl text-white font-black uppercase tracking-widest italic mb-1">Prise de Service</h1>
          <p className="text-slate-400 text-xs sm:text-sm">Veuillez sélectionner votre mission avant d'accéder à la ligne.</p>
        </div>

        {/* Dispositif de recherche premium */}
        <div className="max-w-md mx-auto mb-6 relative px-2">
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher par train ou destination..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-900/60 border border-cyan-500/20 focus:border-cyan-500/50 rounded-2xl py-3.5 pl-11 pr-10 text-xs sm:text-sm text-white placeholder-slate-500 outline-none transition-all shadow-md backdrop-blur-sm font-medium"
            />
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-500/60 w-4 h-4" />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white text-xs font-bold transition-colors"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
          {filteredMissions.map((mission) => {
            const isPermanent = mission.id.startsWith('permanent-');
            return (
              <button
                key={mission.id}
                onClick={() => onSelect(mission)}
                className={`flex flex-row items-center text-left p-2.5 sm:p-3 rounded-xl border transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-sm group
                  ${isPermanent
                    ? 'bg-purple-900/10 border-purple-500/20 hover:bg-purple-900/30 hover:border-purple-400'
                    : 'bg-slate-800/40 border-white/5 hover:bg-slate-700/60 hover:border-cyan-500/40'
                  } `}
              >
                <div className="flex items-center gap-3 w-full">
                  <div className={`text-lg sm:text-xl font-black italic tracking-tighter w-14 shrink-0 text-center ${isPermanent ? 'text-purple-400' : 'text-cyan-400'}`}>
                    {mission.trainNumber}
                  </div>

                  <div className="flex-1 min-w-0 border-l border-white/10 pl-3">
                    <h3 className="text-white font-bold text-xs sm:text-sm line-clamp-1 leading-tight">{mission.lineName}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-slate-500 text-[9px] sm:text-[10px] font-mono uppercase">
                        {mission.stops.length > 0 ? `${mission.stops.length} arrêts` : 'Sans arrêt'}
                      </p>
                      {isPermanent && (
                        <span className="text-[8px] bg-purple-500/20 text-purple-300 px-1.5 py-0.5 rounded uppercase font-bold tracking-widest">Fixe</span>
                      )}
                    </div>
                  </div>

                  <div className="shrink-0 pl-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isPermanent ? 'bg-purple-500/10 group-hover:bg-purple-500/20' : 'bg-cyan-500/10 group-hover:bg-cyan-500/20'}`}>
                      <span className={`text-sm ${isPermanent ? 'text-purple-400' : 'text-cyan-400'}`}>&rarr;</span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
          {filteredMissions.length === 0 && (
            <div className="col-span-full text-center py-10 bg-slate-900/20 border border-white/5 rounded-2xl">
              <p className="text-slate-500 text-sm italic font-medium">Aucune mission trouvée pour "{searchQuery}"</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AppContent() {
  const [session, setSession] = useState<any | null>(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024); // Augmentation du seuil pour plus de sécurité sur tablettes aussi
  const [isPhoneDirectoryOpen, setIsPhoneDirectoryOpen] = useState(false);
  const [isMissionManagementOpen, setIsMissionManagementOpen] = useState(false);
  const [isStopRequestsPageOpen, setIsStopRequestsPageOpen] = useState(false);
  const [requestedStops, setRequestedStops] = useState<string[]>([]);
  const [stopRequests, setStopRequests] = useState<any[]>([]);
  const [isEmergencyStopRequested, setIsEmergencyStopRequested] = useState(false);
  const [backgroundEnabled, setBackgroundEnabled] = useState(true);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);

    // Test Rust Wasm Integration
    initWasm(wasmUrl).then(() => {
      try {
        console.log("[RUST CORE] " + get_rust_greeting("Conducteur"));
        const testDist = calculate_braking_distance(80, 0.8);
        console.log(`[RUST CORE] Distance d'arrêt (80km/h, decel 0.8m/s²): ${testDist.toFixed(2)} mètres.`);
      } catch (e) {
        console.error("Wasm Logic Error:", e);
      }
    }).catch(e => console.error("Wasm module failed to initialize:", e));

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const role = useMemo(() => {
    if (!session) return null;
    const email = session.user.email?.toLowerCase();

    // Priorité aux comptes de test spécifiques
    if (email === 'admin@nexus.cfc') return 'admin';
    if (email === 'chefdetrain@nexus.cfc') return 'conductor';
    if (email === 'chefdegare@nexus.cfc') return 'station_master';
    if (email === 'operateurtraction@nexus.cfc') return 'operator_traction';

    // Sinon, utiliser le rôle défini en métadonnées
    if (session.user.user_metadata?.role) return session.user.user_metadata.role;

    return 'driver';
  }, [session]);

  const displayName = useMemo(() => {
    if (!session?.user?.email) return 'OPS';
    if (session.user.user_metadata?.display_name) return session.user.user_metadata.display_name;
    // Extraire le nom stylisé à partir de l'email, ex: "conducteur1@nexus.cfc" -> "CONDUCTEUR1"
    return session.user.email.split('@')[0].toUpperCase();
  }, [session]);

  // Écouteur global pour les mises à jour de l'application (Vite / React Chunk Errors)
  useEffect(() => {
    let updateAlertPlayed = false;

    const playUpdateAlert = () => {
      if (updateAlertPlayed) return;
      updateAlertPlayed = true;
      try {
        const audio = new Audio('alert_update.mp3');
        audio.play().catch(e => console.error("Erreur lecture MP3 maj:", e));

        // Optionnel : Recharger l'app après quelques secondes
        setTimeout(() => {
          window.location.reload();
        }, 5000);
      } catch (e) {
        console.error("Erreur création Audio maj:", e);
      }
    };

    // Événement spécifique Vite quand un chunk manque suite à un nouveau déploiement
    const onVitePreloadError = (e: Event) => {
      e.preventDefault();
      console.warn("Vite preload error detected (app updated). Playing alert.");
      playUpdateAlert();
    };

    // Erreurs globales classiques de dynamic imports React
    const onGlobalError = (e: ErrorEvent) => {
      if (
        e.message.includes('dynamically imported module') ||
        e.message.includes('Failed to fetch dynamically imported module') ||
        e.message.includes('Importing a module script failed') ||
        e.message.includes('chunk')
      ) {
        console.warn("React chunk load error detected (app updated). Playing alert.");
        playUpdateAlert();
      }
    };

    window.addEventListener('vite:preloadError', onVitePreloadError);
    window.addEventListener('error', onGlobalError);

    return () => {
      window.removeEventListener('vite:preloadError', onVitePreloadError);
      window.removeEventListener('error', onGlobalError);
    };
  }, []);

  // 1. GESTION DE LA SESSION GLOBALE ET REDIRECTION DYNAMIQUE MOBILE
  useEffect(() => {
    const initializeApp = async () => {
      // A. Restaurer d'abord le token depuis le stockage natif de l'appareil (partagé)
      await loadToken();

      // B. Tentative de redirection vers la version de production à jour (Mobile & PC)
      const isMobileLocal = Capacitor.isNativePlatform() && window.location.hostname === 'localhost';
      const isElectronLocal = window.location.protocol === 'file:' || 
        (window.location.hostname === 'localhost' && navigator.userAgent.toLowerCase().includes('electron'));

      if (isMobileLocal || isElectronLocal) {
        try {
          const localUrl = 'http://192.168.1.21';
          const remoteUrl = 'https://prenatal-handful-nastily.ngrok-free.dev';

          const pingUrl = async (url: string) => {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 1200);
            try {
              const res = await fetch(`${url}/api/session.php`, {
                method: 'GET',
                headers: { 'ngrok-skip-browser-warning': 'true' },
                signal: controller.signal
              });
              clearTimeout(timeoutId);
              return res.ok || res.status === 401;
            } catch (e) {
              clearTimeout(timeoutId);
              return false;
            }
          };

          // 1. Tenter le serveur local (Wi-Fi train) - Prioritaire
          const localOnline = await pingUrl(localUrl);
          if (localOnline) {
            console.log("[SYSTEM] Serveur local 192.168.1.21 détecté. Redirection...");
            window.location.href = localUrl;
            return;
          }

          // 2. Tenter le serveur ngrok distant (via Internet) - Repli
          const remoteOnline = await pingUrl(remoteUrl);
          if (remoteOnline) {
            console.log("[SYSTEM] Serveur distant ngrok détecté. Redirection...");
            window.location.href = remoteUrl;
            return;
          }
        } catch (e) {
          console.log("[SYSTEM] Échec de la vérification de mise à jour automatique.", e);
        }
      }

      // C. Chargement normal de la session
      try {
        const session = await localApi.getSession();
        setSession(session);
      } catch (e) {
        console.error('Session error', e);
        setSession(null);
      } finally {
        setAuthInitialized(true);
      }
    };

    initializeApp();
  }, []);

  const resetState = () => {
        setActiveMission(null);
        setIsSimulationMode(false);
        simPKRef.current = null;
        setCurrentCoords(null);
        setAlertedZones({});
        setActiveVisualAlert(null);
        setIsSettingsOpen(false);
        setMissionEndedMessage(false);
        setIsGpsTracking(false);
        setAudioUnlocked(false);
        setIncidentToValidate(null);
        setValidatedInThisPass([]);
        console.log("[SYSTEM] App state synchronously reset via Auth Listener.");

        // Sur Mobile, un rechargement complet est la méthode la plus sûre
        // pour vider tous les contextes et caches éventuels de Capacitor/Webview.
        if (Capacitor.isNativePlatform()) {
          console.log("[SYSTEM] Native Mobile detected, forcing page reload for clean slate.");
          window.location.reload();
        }
  };

  const handleLogout = async () => {
    await localApi.logout();
    setSession(null);
    resetState();
  };


    // Demande de permission pour les notifications au démarrage
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      LocalNotifications.requestPermissions().then(result => {
        console.log("[SYSTEM] Permissions notifications:", result.display);
      });

      // Activer KeepAwake systématiquement sur mobile
      KeepAwake.keepAwake().catch(e => console.error("KeepAwake error:", e));

      // Création du canal de notification pour Android (importance max)
      LocalNotifications.createChannel({
        id: 'ltv-alerts',
        name: 'Alertes Ralentissements',
        importance: 5,
        description: 'Alertes de sécurité pour les ralentissements ferroviaires',
        sound: 'alert_1km.mp3', // Note: Android requiert que le son soit dans res/raw pour être utilisé ici
        visibility: 1,
        vibration: true
      }).catch(e => console.error("Channel creation error:", e));
    }
  }, []);


  // Gestion de la veille et de l'arrière-plan (Web Natif & PWA)
  useEffect(() => {
    let wakeLock: any = null;

    // Fonction pour forcer l'écran à rester allumé via l'API Web standard
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await (navigator as any).wakeLock.request('screen');
          console.log('Wake Lock is active!');
        }
      } catch (err: any) {
        console.error(`${err.name}, ${err.message}`);
      }
    };

    requestWakeLock();

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'hidden') {
        console.log("App was hidden, keeping session active as requested for background tracking.");
        // await supabase.auth.signOut();
        // setSession(null);
      } else if (wakeLock !== null && document.visibilityState === 'visible') {
        // Au cas où l'on revienne et que le wakeLock a sauté
        requestWakeLock();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (wakeLock !== null) {
        wakeLock.release().then(() => {
          wakeLock = null;
        });
      }
    };
  }, []);

  const { balagneSystem, centraleSystem, paths } = useTrack();

  // Initialisation dynamique avec les coordonnées du contexte
  const pathBalagne = useMemo<[number, number][]>(() =>
    paths.balagne.length > 0 ? paths.balagne.map(c => [c[1], c[0]]) : [],
    [paths.balagne]);
    
  const pathCentrale = useMemo<[number, number][]>(() =>
    paths.centrale.length > 0 ? paths.centrale.map(c => [c[1], c[0]]) : [],
    [paths.centrale]);
  const [currentCoords, setCurrentCoords] = useState<[number, number] | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isGpsTracking, setIsGpsTracking] = useState(false);
  const [recenterTrigger, setRecenterTrigger] = useState(0);
  const [missionEndedMessage, setMissionEndedMessage] = useState(false);
  const [isEvsPageOpen, setIsEvsPageOpen] = useState(false);
  const [isSecuDashboardOpen, setIsSecuDashboardOpen] = useState(false);
  const [activePdf, setActivePdf] = useState<{ url: string; title: string } | null>(null);
  const [isAccountPageOpen, setIsAccountPageOpen] = useState(false);
  const [quickSearchQuery, setQuickSearchQuery] = useState('');
  const [adminSecuSubTab, setAdminSecuSubTab] = useState<'register' | 'bulletins'>('register');
  const [adminLtvLine, setAdminLtvLine] = useState<'Centrale' | 'Balagne'>('Centrale');

  // GPS Simulation State
  const [isSimulationMode, setIsSimulationMode] = useState(false);
  const simPKRef = useRef<number | null>(null);
  const [simDirection, setSimDirection] = useState<1 | -1>(1);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const [updateAvailable, setUpdateAvailable] = useState<{
    type: 'code_update' | 'connection_recovered';
    targetUrl?: string;
  } | null>(null);

  // Style de carte : OSM (Plan léger par défaut) ou Satellite
  const [mapStyle, setMapStyle] = useState<'osm' | 'satellite'>(() => 
    (localStorage.getItem('map_style') as 'osm' | 'satellite') || 'osm'
  );

  useEffect(() => {
    localStorage.setItem('map_style', mapStyle);
  }, [mapStyle]);

  // Système de vérification des mises à jour en arrière-plan (temps réel)
  useEffect(() => {
    let active = true;

    const checkUpdates = async () => {
      const isLocalHost = window.location.hostname === 'localhost' || window.location.protocol === 'file:';
      
      if (!isLocalHost) {
        // Cas A : L'app est chargée depuis le serveur. On surveille si le build a changé sur le serveur.
        try {
          const currentHash = (() => {
            const scripts = Array.from(document.querySelectorAll('script'));
            for (const script of scripts) {
              const src = script.getAttribute('src') || '';
              const match = src.match(/index-([a-zA-Z0-9_-]+)\.js/);
              if (match) return match[1];
            }
            return null;
          })();

          if (!currentHash) return;

          // Fetch de index.html avec cache-busting
          const res = await fetch(`${window.location.origin}/index.html?t=${Date.now()}`, {
            headers: { 'ngrok-skip-browser-warning': 'true' }
          });
          if (!res.ok) return;

          const htmlText = await res.text();
          const match = htmlText.match(/src=["'](?:[^"']*)index-([a-zA-Z0-9_-]+)\.js["']/);
          const serverHash = match ? match[1] : null;

          if (serverHash && serverHash !== currentHash && active) {
            console.log(`[UPDATE] Nouvelle version détectée : ${serverHash} (actuelle : ${currentHash})`);
            setUpdateAvailable({ type: 'code_update' });
          }
        } catch (err) {
          console.debug("[UPDATE] Échec du check de mise à jour (serveur) :", err);
        }
      } else {
        // Cas B : L'app est chargée localement (cache). On surveille si le serveur local ou ngrok repasse en ligne.
        try {
          const localUrl = 'http://192.168.1.21';
          const remoteUrl = 'https://prenatal-handful-nastily.ngrok-free.dev';

          const pingUrl = async (url: string) => {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 1200);
            try {
              const res = await fetch(`${url}/api/session.php`, {
                method: 'GET',
                headers: { 'ngrok-skip-browser-warning': 'true' },
                signal: controller.signal
              });
              clearTimeout(timeoutId);
              return res.ok || res.status === 401;
            } catch (e) {
              clearTimeout(timeoutId);
              return false;
            }
          };

          const localOnline = await pingUrl(localUrl);
          if (localOnline && active) {
            setUpdateAvailable({ type: 'connection_recovered', targetUrl: localUrl });
            return;
          }

          const remoteOnline = await pingUrl(remoteUrl);
          if (remoteOnline && active) {
            setUpdateAvailable({ type: 'connection_recovered', targetUrl: remoteUrl });
            return;
          }
        } catch (e) {
          console.debug("[UPDATE] Échec du check de mise à jour (offline) :", e);
        }
      }
    };

    // Effectue le premier check après 8 secondes, puis toutes les 45 secondes
    const initialTimeout = setTimeout(checkUpdates, 8000);
    const interval = setInterval(checkUpdates, 45000);

    return () => {
      active = false;
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);

  interface LTV {
    id: string;
    startPK: number;
    endPK: number;
    speed: number;
    reason: string;
    startDate: string;
    endDate: string;
    line?: 'Centrale' | 'Balagne';
  }

  interface Incident {
    id: string;
    type: 'bovin' | 'eboulement' | 'promeneur' | 'autre';
    pk: number;
    timestamp: number;
    description: string;
  }

  // État des ralentissements
  const [ltvList, setLtvList] = useState<LTV[]>([]);

  // 1. Initial Fetch
  const fetchLTVs = async () => {
    try {
      const data = await localApi.getLtvs();
      if (data) {
        const today = new Date().toISOString().split('T')[0];
        const active = data.filter((ltv: LTV) => !ltv.endDate || ltv.endDate === 'RESTITUTION' || ltv.endDate >= today);
        setLtvList(active);
      }
    } catch (err) {
      console.error('Erreur chargement LTV:', err);
    }
  };

  useEffect(() => {
    fetchLTVs();
    
    // Polling toutes les 10 secondes pour les LTVs
    const intervalId = setInterval(fetchLTVs, 10000);
    return () => clearInterval(intervalId);
  }, []);


  // Vérification périodique d'expiration (toutes les minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      const today = new Date().toISOString().split('T')[0];
      setLtvList(prev => {
        const active = prev.filter(ltv => !ltv.endDate || ltv.endDate === 'RESTITUTION' || ltv.endDate >= today);
        if (active.length !== prev.length) {
          const logMsg = `${prev.length - active.length} ralentissement(s) expiré(s) retiré(s) automatiquement`;
          // Since we can't easily call logAction here due to scoping, we'll just console log it
          console.log(`[SYSTEM] ${logMsg}`);
          return active;
        }
        return prev;
      });
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // État des incidents
  const [incidents, setIncidents] = useState<Incident[]>(() => {
    const saved = localStorage.getItem('cfc-nexus-incidents');
    return saved ? JSON.parse(saved) : [];
  });

  // État pour la validation des incidents (levée de doute)
  const [incidentToValidate, setIncidentToValidate] = useState<Incident | null>(null);
  const [validatedInThisPass, setValidatedInThisPass] = useState<string[]>([]);

  // Logs système pour la traçabilité
  const [systemLogs, setSystemLogs] = useState<{ id: string, timestamp: number, user: string, action: string, details: string }[]>(() => {
    const saved = localStorage.getItem('cfc-nexus-logs');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('cfc-nexus-incidents', JSON.stringify(incidents));
  }, [incidents]);

  const fetchIncidents = async () => {
    try {
      const data = await localApi.getIncidents();
      setIncidents(data || []);
    } catch (err) {
      console.error('Erreur chargement Incidents:', err);
    }
  };

  useEffect(() => {
    fetchIncidents();
    const intervalId = setInterval(fetchIncidents, 10000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    localStorage.setItem('cfc-nexus-logs', JSON.stringify(systemLogs));
  }, [systemLogs]);

  const logAction = (user: string, action: string, details: string) => {
    const newLog = {
      id: Date.now().toString(),
      timestamp: Date.now(),
      user,
      action,
      details
    };
    setSystemLogs(prev => [newLog, ...prev].slice(0, 100)); // Garder les 100 derniers logs
    console.log(`[LOG] ${user} | ${action} | ${details}`);
  };

  const logSecurityEvent = async (action: string, payload: any) => {
    const event = {
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15),
      event_type: action,
      driver_id: session?.user?.id,
      driver_name: displayName,
      train_number: activeMission?.trainNumber || 'SANS TRAIN',
      pk: telemetry.currentPK,
      coords: currentCoords ? `(${currentCoords[1]}, ${currentCoords[0]})` : null,
      payload: payload,
      device_timestamp: new Date().toISOString()
    };

    // 1. Sauvegarde locale (File d'attente)
    const queue = JSON.parse(localStorage.getItem('security_log_queue') || '[]');
    localStorage.setItem('security_log_queue', JSON.stringify([...queue, event]));

    // 2. Tentative d'envoi immédiat
    try {
      await localApi.sendBlackboxEvent(event);
    } catch (err) {
      console.error("Erreur d'envoi à la boîte noire:", err);
    }
  };


  const handleConfirmPdfReading = async (confirmed: boolean) => {
    if (confirmed && activePdf) {
      await logSecurityEvent('DOC_READ', {
        document_title: activePdf.title,
        status: 'CONFIRMED_BY_AGENT'
      });
      setActivePdf(null);
    } else {
      setActivePdf(null); // Just close if not confirmed or handle as needed
    }
  };

  // Sync background worker pour la Boîte Noire
  useEffect(() => {
    const syncLogs = async () => {
      const queue = JSON.parse(localStorage.getItem('security_log_queue') || '[]');
      if (queue.length === 0) return;

      for (const event of queue) {
        try {
          await localApi.sendBlackboxEvent(event);
          const currentQueue = JSON.parse(localStorage.getItem('security_log_queue') || '[]');
          localStorage.setItem('security_log_queue', JSON.stringify(currentQueue.filter((e: any) => e.id !== event.id)));
        } catch (err) {
          console.error("Échec de l'envoi de la file d'attente (Boîte Noire):", err);
          break;
        }
      }
    };

    const interval = setInterval(syncLogs, 30000);
    window.addEventListener('online', syncLogs);
    return () => {
      clearInterval(interval);
      window.removeEventListener('online', syncLogs);
    };
  }, []);

  const [adminTab, setAdminTab] = useState<'live' | 'ltv' | 'drivers' | 'missions' | 'stations' | 'operations' | 'security_docs' | 'users' | 'planning' | 'leaves'>('live');
  const [missions, setMissions] = useState<Mission[]>(PERMANENT_MISSIONS);
  const [activeMission, setActiveMission] = useState<Mission | null>(null);
  const [unreadSecuCount, setUnreadSecuCount] = useState(0);

  // Etats d'envoi du planning
  const [planningUploading, setPlanningUploading] = useState<Record<string, boolean>>({});
  const [planningError, setPlanningError] = useState<Record<string, string | null>>({});
  const [planningSuccess, setPlanningSuccess] = useState<Record<string, string | null>>({});

  const handleUploadPlanning = async (file: File, zone: string) => {
    const zoneKey = zone.toLowerCase();
    setPlanningUploading(prev => ({ ...prev, [zoneKey]: true }));
    setPlanningError(prev => ({ ...prev, [zoneKey]: null }));
    setPlanningSuccess(prev => ({ ...prev, [zoneKey]: null }));

    try {
      if (file.type !== 'application/pdf') {
        throw new Error('Seuls les fichiers PDF sont autorisés.');
      }

      const res = await localApi.uploadPlanning(file, zoneKey);
      if (res.error) throw new Error(res.error);

      setPlanningSuccess(prev => ({ ...prev, [zoneKey]: 'Planning mis à jour avec succès !' }));
      setTimeout(() => {
        setPlanningSuccess(prev => ({ ...prev, [zoneKey]: null }));
      }, 4000);
    } catch (err: any) {
      setPlanningError(prev => ({ ...prev, [zoneKey]: err.message || 'Erreur lors du téléversement.' }));
    } finally {
      setPlanningUploading(prev => ({ ...prev, [zoneKey]: false }));
    }
  };

  // 1. CHARGEMENT ET SYNC DES MISSIONS
  const fetchMissions = async () => {
    console.log("[SYSTEM] Rechargement des missions locales...");
    try {
      const dbMissions = await localApi.getMissions();
      setMissions(sortMissions([...PERMANENT_MISSIONS, ...dbMissions]));
    } catch (err) {
      console.error("[SYSTEM] Erreur fatale lors du chargement des missions:", err);
    }
  };

  const fetchUnreadSecuCount = async () => {
    try {
      const data = await localApi.getBulletins();
      if (data) {
        const viewedIds = JSON.parse(localStorage.getItem('viewedSecuIds') || '[]');
        const unread = data.filter((doc: any) => !viewedIds.includes(doc.id));
        setUnreadSecuCount(unread.length);
      }
    } catch (err) {
      console.error('Erreur chargement bulletins pour comptage:', err);
    }
  };

  useEffect(() => {
    fetchMissions();
    fetchUnreadSecuCount();

    const intervalId = setInterval(fetchUnreadSecuCount, 10000);
    const missionsIntervalId = setInterval(fetchMissions, 15000);

    return () => {
      clearInterval(intervalId);
      clearInterval(missionsIntervalId);
    };
  }, []);

  const handleAddMission = async (mission: Mission) => {
    try {
      const res = await localApi.createMission(mission);
      if (res.error) {
        alert(`Erreur: ${res.error}`);
      } else {
        fetchMissions(); // Force refresh
      }
    } catch (err: any) {
      console.error("Erreur ajout mission:", err);
      alert(`Erreur: ${err.message}`);
    }
  };

  const handleAddMultipleMissions = async (newMissions: Mission[]) => {
    if (!newMissions || newMissions.length === 0) return;

    try {
      console.log(`[SYSTEM] Tentative d'import de ${newMissions.length} missions locales...`);
      const res = await localApi.importMissions(newMissions);
      if (res.error) {
        alert(`Erreur lors de l'import : ${res.error}`);
      } else {
        console.log("[SYSTEM] Import multiple réussi");
        fetchMissions(); // Force refresh
        alert(`${newMissions.length} mission(s) synchronisée(s) avec succès.`);
      }
    } catch (err: any) {
      console.error("Erreur import multiple:", err);
      alert(`Erreur lors de l'import : ${err.message}`);
    }
  };

  const handleUpdateMission = async (mission: Mission) => {
    if (!mission.id || mission.id.startsWith('permanent-')) return;

    try {
      const res = await localApi.updateMission(mission);
      if (res.error) {
        alert(`Erreur: ${res.error}`);
      } else {
        fetchMissions(); // Force refresh
      }
    } catch (err: any) {
      console.error("Erreur mise à jour mission:", err);
      alert(`Erreur: ${err.message}`);
    }
  };

  const handleDeleteMission = async (id: string) => {
    if (!id || id.startsWith('permanent-')) {
      console.warn(`[SYSTEM] Refus de suppression de la mission protégée: ${id}`);
      alert("Impossible de supprimer une mission système permanente.");
      return;
    }

    const confirm = window.confirm(`Voulez-vous vraiment supprimer définitivement cette mission ?\nID: ${id}`);
    if (!confirm) return;

    console.log(`[SYSTEM] Tentative de suppression locale - Table: missions, ID: ${id}`);
    
    try {
      const res = await localApi.deleteMission(id);
      if (res.error) {
        console.error("[SYSTEM] Échec de la suppression:", res.error);
        alert(`ERREUR : ${res.error}`);
      } else {
        console.log(`[SYSTEM] Suppression réussie.`);
        fetchMissions(); // Force refresh IMMEDIAT
        alert(`SUCCÈS : La mission a été supprimée.\nID: ${id}`);
      }
    } catch (e: any) {
      console.error("[SYSTEM] Exception lors de la suppression:", e);
      alert(`Une erreur inattendue est survenue lors de la suppression : ${e.message}`);
    }
  };

  const activeReferenceSystem = useMemo(() => {
     if (!currentCoords) return balagneSystem;
     const distB = balagneSystem.getDistanceToTrack(currentCoords);
     const distC = centraleSystem.getDistanceToTrack(currentCoords);
     return distC < distB ? centraleSystem : balagneSystem;
  }, [currentCoords, balagneSystem, centraleSystem]);

  // Calcul dynamique du sens ferroviaire (vrai si on va vers les PK croissants sur la section actuelle)
  // Prise en compte du basculement à Ponte-Leccia pour les missions Calvi <-> Ajaccio
  const currentSectionDirection = useMemo(() => {
    if (!activeMission || !activeMission.stops || activeMission.stops.length < 2) {
      return { isTravelUp: true, isSensPair: false };
    }

    const stops = activeMission.stops;
    const origin = (stops[0]?.name || '').toLowerCase();
    const destination = (stops[stops.length - 1]?.name || '').toLowerCase();
    
    // Déterminer la ligne actuelle selon les coordonnées ou le système de référence
    const currentLine = activeReferenceSystem ? activeReferenceSystem.lineName : 'Balagne';

    // Cas 1 : Calvi -> Ajaccio
    if (origin.includes('calvi') && destination.includes('ajaccio')) {
      if (currentLine === 'Balagne') {
        // De Calvi (PK 119.9) à Ponte-Leccia (PK 46.7) : PK décroissants = Sens Pair
        return { isTravelUp: false, isSensPair: true };
      } else {
        // De Ponte-Leccia (PK 46.7) vers Ajaccio (PK 157.4) : PK croissants = Sens Impair
        return { isTravelUp: true, isSensPair: false };
      }
    }

    // Cas 2 : Ajaccio -> Calvi
    if (origin.includes('ajaccio') && destination.includes('calvi')) {
      if (currentLine === 'Centrale') {
        // D'Ajaccio (PK 157.4) vers Ponte-Leccia (PK 46.7) : PK décroissants = Sens Pair
        return { isTravelUp: false, isSensPair: true };
      } else {
        // De Ponte-Leccia (PK 46.7) vers Calvi (PK 119.9) : PK croissants = Sens Impair
        return { isTravelUp: true, isSensPair: false };
      }
    }

    // Cas standard (Bastia <-> Ajaccio ou Calvi <-> Ponte-Leccia)
    const firstPk = stops[0]?.pk || 0;
    const lastPk = stops[stops.length - 1]?.pk || 0;
    const travelUp = lastPk > firstPk;
    
    // Sur le réseau CFC :
    // PK décroissants (vers Bastia/Ponte-Leccia) = Sens Pair
    // PK croissants (vers Ajaccio/Calvi) = Sens Impair
    return {
      isTravelUp: travelUp,
      isSensPair: !travelUp
    };
  }, [activeMission, activeReferenceSystem]);

  const isTravelUp = currentSectionDirection.isTravelUp;

  // Télémétrie utilisant le système de référence précis avec filtrage sens pair/impair
  const telemetry = useTrainTelemetry(
    currentCoords, 
    ltvList, 
    activeReferenceSystem, 
    currentSectionDirection.isTravelUp,
    currentSectionDirection.isSensPair
  );

  // 3. SYNCHRONISATION DES ARRÊTS DEMANDÉS (Centralisée)
  const fetchRequestedStops = async () => {
    if (!activeMission) return;
    try {
      const data = await localApi.getStopRequests(activeMission.trainNumber);
      setStopRequests(data);
      // Seuls les arrêts non refusés sont affichés sur le trajet
      const activeStops = data.filter((d: any) => d.status !== 'rejected').map((d: any) => d.station_name);
      setRequestedStops(activeStops);
    } catch (err) {
      console.error("[SYSTEM] Erreur chargement stop_requests locales:", err);
      setRequestedStops([]);
      setStopRequests([]);
    }
  };

  useEffect(() => {
    if (!activeMission) {
      setRequestedStops([]);
      return;
    }

    fetchRequestedStops();

    // Polling régulier des arrêts demandés toutes les 5 secondes
    const pollInterval = setInterval(() => {
      console.log("[SYSTEM] Polling des arrêts demandés...");
      fetchRequestedStops();
    }, 5000);

    return () => {
      clearInterval(pollInterval);
    };
  }, [activeMission]);

  // Surveillance de la demande d'arrêt immédiat pour le conducteur
  useEffect(() => {
    if (role !== 'driver' || !activeMission) {
      setIsEmergencyStopRequested(false);
      return;
    }

    const checkEmergency = async () => {
      try {
        const res = await localApi.checkEmergencyStop(activeMission.trainNumber);
        setIsEmergencyStopRequested(!!res.requested);
      } catch (err) {
        console.error("Erreur check emergency driver:", err);
      }
    };

    checkEmergency();
    const interval = setInterval(checkEmergency, 2000); // Check toutes les 2s
    return () => clearInterval(interval);
  }, [role, activeMission]);

  // Alarme sonore en cas de demande d'arrêt immédiat
  useEffect(() => {
    let interval: any = null;
    
    const playAlarmSound = () => {
      try {
        const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();

        oscillator.type = 'sawtooth';
        oscillator.frequency.setValueAtTime(880, audioCtx.currentTime);
        gainNode.gain.setValueAtTime(0.35, audioCtx.currentTime);
        
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        
        oscillator.start();
        setTimeout(() => {
          oscillator.stop();
          audioCtx.close();
        }, 400);
      } catch (e) {
        console.error("Audio alarm error:", e);
      }
    };

    if (isEmergencyStopRequested) {
      playAlarmSound();
      interval = setInterval(playAlarmSound, 800);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isEmergencyStopRequested]);

  const toggleRequestedStop = async (stationName: string) => {
    if (!activeMission) return;

    const isRequested = requestedStops.some(s => s.toLowerCase() === stationName.toLowerCase());

    // 1. MISE À JOUR OPTIMISTE (UI immédiate)
    if (isRequested) {
      setRequestedStops(prev => prev.filter(s => s.toLowerCase() !== stationName.toLowerCase()));
    } else {
      setRequestedStops(prev => [...prev, stationName]);
    }

    try {
      if (isRequested) {
        const res = await localApi.deleteStopRequest(activeMission.trainNumber, stationName);
        if (res.error) {
          console.error("Erreur suppression arrêt:", res.error);
          fetchRequestedStops(); // Rollback
        }
      } else {
        const res = await localApi.createStopRequest(
          activeMission.trainNumber,
          stationName,
          displayName || ''
        );
        if (res.error) {
          console.error("Erreur ajout arrêt:", res.error);
          fetchRequestedStops();
        }

        if (role === 'driver' && audioUnlocked && audioEnabled) {
          try { new Audio('alert_stop_request.mp3').play().catch(() => { }); } catch (e) { }
        }
      }
    } catch (err) {
      console.error("Erreur toggle arrêt:", err);
      fetchRequestedStops();
    }

    // 3. RAFRAÎCHISSEMENT MANUEL (Sécurité)
    setTimeout(() => fetchRequestedStops(), 800);
  };

  const handleAcceptStop = async (stationName: string) => {
    if (!activeMission) return;
    try {
      await localApi.updateStopRequestStatus(activeMission.trainNumber, stationName, 'accepted');
      await fetchRequestedStops();
    } catch (err) {
      console.error("Erreur validation arrêt:", err);
    }
  };

  const handleRejectStop = async (stationName: string) => {
    if (!activeMission) return;
    try {
      await localApi.updateStopRequestStatus(activeMission.trainNumber, stationName, 'rejected');
      await fetchRequestedStops();
    } catch (err) {
      console.error("Erreur refus arrêt:", err);
    }
  };

  // 4. NETTOYAGE AUTOMATIQUE DES ARRÊTS PASSÉS
  useEffect(() => {
    if (role !== 'driver' || !activeMission || telemetry.currentPK === null) return;

    const passedRequestedStop = activeMission.stops.find(stop => {
      const isRequested = requestedStops.some(s => s.toLowerCase() === stop.stationName.toLowerCase());
      if (!isRequested) return false;

      const margin = 0.050; // 50 mètres
      if (isTravelUp) {
        return telemetry.currentPK! > (stop.pk + margin);
      } else {
        return telemetry.currentPK! < (stop.pk - margin);
      }
    });

    if (passedRequestedStop) {
      console.log(`[SYSTEM] Nettoyage arrêt passé : ${passedRequestedStop.stationName}`);
      localApi.deleteStopRequest(activeMission.trainNumber, passedRequestedStop.stationName)
        .catch(err => {
          console.error("Erreur auto-cleanup:", err);
        });
    }
  }, [telemetry.currentPK, requestedStops, activeMission, role, isTravelUp]);

  // Gestion des alertes audio et visuelles
  const [alertedZones, setAlertedZones] = useState<Record<string, { warned1000?: boolean, warned50?: boolean }>>({});
  const [activeVisualAlert, setActiveVisualAlert] = useState<LTV | null>(null);

  // Fonction de synthèse vocale + bip
  const announce = (message: string, doubleBeep = false, playBeepFlag = true) => {
    if (!audioUnlocked || !audioEnabled) return;

    try {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const playBeep = (startTimeOffset: number) => {
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(800, audioCtx.currentTime + startTimeOffset);
        gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime + startTimeOffset);
        oscillator.start(audioCtx.currentTime + startTimeOffset);
        oscillator.stop(audioCtx.currentTime + startTimeOffset + 0.15);
      };

      if (playBeepFlag) {
        playBeep(0);
        if (doubleBeep) {
          playBeep(0.25); // Joue un deuxième bip un quart de seconde plus tard
        }
      }

      setTimeout(() => {
        const utterance = new SpeechSynthesisUtterance(message);
        utterance.lang = 'fr-FR';
        const voices = window.speechSynthesis.getVoices();
        const femaleVoice = voices.find(v => v.lang === 'fr-FR' && v.name.includes('Google')) || voices.find(v => v.lang === 'fr-FR');
        if (femaleVoice) utterance.voice = femaleVoice;
        window.speechSynthesis.speak(utterance);
      }, 300);
    } catch (e) {
      console.error("Erreur lecture audio:", e);
    }
  };

  const playActivationBeep = () => {
    try {
      const AudioCtxConstructor = (window.AudioContext || (window as any).webkitAudioContext);
      if (AudioCtxConstructor) {
        const audioCtx = new AudioCtxConstructor();
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);

        // Bip agréable de confirmation (800Hz)
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(800, audioCtx.currentTime);
        gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);

        oscillator.start();
        oscillator.stop(audioCtx.currentTime + 0.15); // Bip court de 150ms
      }
    } catch (e) {
      console.error("Erreur bip d'activation:", e);
    }
  };

  const unlockAudio = () => {
    try {
      // Débloque l'UI immédiatement pour que le bouton réagisse
      setAudioUnlocked(true);

      // Joue un son de confirmation (bip court) et débloque le Web Audio API
      playActivationBeep();

      // Débloque la synthèse vocale avec un message vide
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance('');
        utterance.volume = 0;
        window.speechSynthesis.speak(utterance);
      }
    } catch (e) {
      console.error("Impossible de débloquer l'audio:", e);
    }
  };

  // Fonction d'envoi de notification locale (Arrière-plan/Écran verrouillé)
  const sendLocalNotification = async (title: string, body: string, id: number) => {
    console.log(`[SYSTEM] Tentative notification : ${title} (Plateforme native: ${Capacitor.isNativePlatform()}, BackgroundEnabled: ${backgroundEnabled})`);

    if (!Capacitor.isNativePlatform() || !backgroundEnabled) {
      if (!Capacitor.isNativePlatform()) {
        console.log("[SYSTEM] Notification ignorée : Pas sur plateforme native.");
      }
      return;
    }

    try {
      const notificationId = isNaN(id) ? Math.floor(Math.random() * 1000000) : id;
      await LocalNotifications.schedule({
        notifications: [
          {
            title,
            body,
            id: notificationId,
            schedule: { at: new Date(Date.now() + 100) },
            sound: 'alert_1km.mp3',
            attachments: [],
            actionTypeId: "",
            extra: null,
            channelId: 'ltv-alerts',
            fullScreenIntent: true
          } as any
        ]
      });
      console.log(`[SYSTEM] Notification envoyée avec succès (ID: ${notificationId})`);
    } catch (e) {
      console.error("Local Notification Error:", e);
    }
  };

  useEffect(() => {
    if (telemetry.distanceToNextLTV === null || !telemetry.nextLTV) {
      setActiveVisualAlert(null);
      return;
    }

    const dist = telemetry.distanceToNextLTV;
    const ltv = telemetry.nextLTV;
    const currentAlerts = alertedZones[ltv.id] || {};

    // Reset de l'état d'alerte si le train s'est éloigné (plus de 1500m) ou a dépassé la zone
    const isPastZone = dist === null || dist > 1500;
    if (isPastZone && (currentAlerts.warned1000 || currentAlerts.warned50)) {
      setAlertedZones(prev => {
        const copy = { ...prev };
        delete copy[ltv.id];
        return copy;
      });
      return;
    }

    // Fonction pour jouer un son personnalisé
    const playCustomAlert = (audioPath: string) => {
      if (!audioUnlocked || !audioEnabled) return;
      try {
        const audio = new Audio(audioPath);
        audio.play().catch(e => console.error("Erreur lecture MP3:", e));
      } catch (e) {
        console.error("Erreur création Audio:", e);
      }
    };

    if (dist <= 1000 && dist > 800 && !currentAlerts.warned1000) {
      playCustomAlert('alert_1km.mp3');
      sendLocalNotification(
        "RALENTISSEMENT PROCHE",
        `Vitesse : ${ltv.speed} km/h à 1000 mètres. Raison : ${ltv.reason}`,
        parseInt(ltv.id.replace(/\D/g, '').slice(-8)) || 1000
      );
      setAlertedZones(prev => ({ ...prev, [ltv.id]: { ...(prev[ltv.id] || {}), warned1000: true } }));
    }

    // Déclenchement de l'alerte sonore à 50m
    if (dist <= 50 && dist > 0 && !currentAlerts.warned50) {
      playCustomAlert('alert_imminent.mp3');
      sendLocalNotification(
        "ENTRÉE EN ZONE LTV",
        `Vitesse limite : ${ltv.speed} km/h. Soyez vigilant.`,
        parseInt(ltv.id.replace(/\D/g, '').slice(-8)) + 1 || 1001
      );
      setAlertedZones(prev => ({ ...prev, [ltv.id]: { ...(prev[ltv.id] || {}), warned50: true } }));
    }

    // Affichage visuel uniquement lors du passage sur le LTV (0m de l'entrée/sortie)
    if (dist === 0 && telemetry.currentPK !== null) {
      if (activeVisualAlert?.id !== ltv.id) {
        setActiveVisualAlert(ltv);
      }
    } else {
      setActiveVisualAlert(null);
    }
  }, [telemetry.distanceToNextLTV, telemetry.nextLTV, telemetry.currentPK, alertedZones, activeVisualAlert]);

  // Gestion de la localisation GPS réelle ou simulée
  useEffect(() => {
    let watchId: string | null = null;
    let simInterval: any = null;

    if (isSimulationMode) {
      if (simPKRef.current === null && activeMission && activeMission.stops.length > 0) {
        simPKRef.current = activeMission.stops[0].pk;
        // Direction auto : descendre les PK si on commence haut (Calvi), monter sinon
        setSimDirection(isTravelUp ? 1 : -1);
      }

      const SIM_SPEED_KMH = 80; // Vitesse de croisière simulée
      const UPDATE_INTERVAL_MS = 100;
      const stepPK = (SIM_SPEED_KMH / 3600) * (UPDATE_INTERVAL_MS / 1000); // PK parcouru par intervalle

      simInterval = setInterval(() => {
        if (simPKRef.current === null) return;

        // On avance ou on recule selon la direction du simulateur
        // Note: simDirection 1 = PK croissant, -1 = PK décroissant
        const nextPK = simPKRef.current + (simDirection * stepPK);

        // Limites du réseau (environ 120km max)
        if (nextPK > 130 || nextPK < 0) {
          setSimDirection(prev => (prev === 1 ? -1 : 1));
          return;
        }

        simPKRef.current = nextPK;
        const coords = activeReferenceSystem.pkToCoord(nextPK);
        if (coords) {
          setCurrentCoords(coords);
        }
      }, UPDATE_INTERVAL_MS);
    } else {
      const startTracking = async () => {
        try {
          // On web, checking permissions might fail before we even call watchPosition
          if (Capacitor.isNativePlatform()) {
            const permission = await Geolocation.requestPermissions();
            if (permission.location !== 'granted') {
              console.error("Permission de localisation refusée");
              return;
            }
          }

          watchId = await Geolocation.watchPosition(
            {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 0
            },
            (position) => {
              if (position) {
                setCurrentCoords([position.coords.latitude, position.coords.longitude]);
              }
            }
          );
        } catch (e) {
          console.error("Erreur GPS (Ignoré sur Web):", e);
        }
      };

      startTracking();
    }

    return () => {
      if (watchId) Geolocation.clearWatch({ id: watchId });
      if (simInterval) clearInterval(simInterval);
    };
  }, [isSimulationMode, pathBalagne, pathCentrale, simDirection, activeMission, activeReferenceSystem]);

  // Reset simulation PK when active mission changes
  useEffect(() => {
    simPKRef.current = null;
  }, [activeMission]);

  // LOGIQUE DE DÉTECTION DE PASSAGE D'INCIDENT (Levée de doute)
  useEffect(() => {
    if (role !== 'driver' || !telemetry.currentPK || incidents.length === 0) return;

    const currentPK = telemetry.currentPK;

    // Trouver un incident "proche" qu'on est en train de passer ou qu'on vient de passer
    // On cherche un incident à moins de 50m (0.050 PK) qu'on n'a pas encore validé dans ce passage
    const nearbyIncident = incidents.find(inc =>
      Math.abs(inc.pk - currentPK) < 0.050 && !validatedInThisPass.includes(inc.id)
    );

    if (nearbyIncident) {
      // On marque qu'on est sur l'incident
      setIncidentToValidate(nearbyIncident);
    } else if (incidentToValidate) {
      // On vient de sortir de la zone d'un incident qu'on devait valider
      // Le prompt reste affiché jusqu'à ce que le conducteur réponde
    }
  }, [telemetry.currentPK, incidents, validatedInThisPass, role, incidentToValidate]);

  const handleResolveIncident = (stillPresent: boolean) => {
    if (!incidentToValidate) return;

    if (!stillPresent) {
      logAction(displayName, 'SUPPRESSION (LEVÉE DE DOUTE)', `Incident ${incidentToValidate.type} au PK ${incidentToValidate.pk.toFixed(3)} validé COMME ABSENT`);
      logSecurityEvent('INCIDENT_RESOLVED', { incident: incidentToValidate, status: 'ABSENT' });
      localApi.resolveIncident(incidentToValidate.id)
        .then(() => fetchIncidents())
        .catch(err => console.error("Erreur résolution incident:", err));
      setIncidents(prev => prev.filter(inc => inc.id !== incidentToValidate.id));
    } else {
      logAction(displayName, 'MAINTIEN (LEVÉE DE DOUTE)', `Incident ${incidentToValidate.type} au PK ${incidentToValidate.pk.toFixed(3)} validé COMME PRÉSENT`);
      logSecurityEvent('INCIDENT_RESOLVED', { incident: incidentToValidate, status: 'PRESENT' });
    }

    setValidatedInThisPass(prev => [...prev, incidentToValidate.id]);
    setIncidentToValidate(null);
  };

  // Helper pour trier les missions : Permanentes en haut, puis par numéro (tri naturel)
  const sortMissions = (list: Mission[]) => {
    return [...list].sort((a, b) => {
      const aPerm = PERMANENT_MISSIONS.some(p => p.id === a.id);
      const bPerm = PERMANENT_MISSIONS.some(p => p.id === b.id);
      if (aPerm && !bPerm) return -1;
      if (!aPerm && bPerm) return 1;
      if (aPerm && bPerm) return 0;
      return a.trainNumber.localeCompare(b.trainNumber, undefined, { numeric: true, sensitivity: 'base' });
    });
  };


  const nextStop = useMemo(() => {
    if (!activeMission || telemetry.currentPK === null || activeMission.stops.length === 0) return null;

    const currentPK = telemetry.currentPK;

    // Si la ligne compte à l'envers (pk décroissant, ex: 157 -> 0)
    if (!isTravelUp) {
      return activeMission.stops
        .filter(stop => stop.pk < currentPK - 0.050) // On ajoute une marge de 50m pour ne pas rester "bloqué" sur la gare actuelle
        .sort((a, b) => b.pk - a.pk)[0]; // Prendre le stop le plus proche devant (le plus grand PK parmi ceux restants)
    }
    // Si la ligne compte à l'endroit (pk croissant, ex: 0 -> 157)
    else {
      return activeMission.stops
        .filter(stop => stop.pk > currentPK + 0.050)
        .sort((a, b) => a.pk - b.pk)[0]; // Prendre le stop le plus proche devant (le plus petit PK parmi ceux restants)
    }
  }, [activeMission, telemetry.currentPK, isTravelUp]);

  const displayStatus = useMemo(() => {
    if (!activeMission || activeMission.stops.length === 0) return null;

    // Si on n'est pas encore parti (l'heure actuelle est avant le tout premier départ)
    const firstStop = activeMission.stops[0];
    if (firstStop?.arrivalTime) {
      const [h, m] = firstStop.arrivalTime.split(':').map(Number);
      const depTime = new Date();
      depTime.setHours(h, m, 0, 0);
      if (currentTime.getTime() < depTime.getTime()) {
        return {
          label: "Départ Prévu",
          stationName: firstStop.stationName,
          time: firstStop.arrivalTime,
          pk: firstStop.pk,
          isPending: true
        };
      }
    }

    if (nextStop) {
      return {
        label: "Prochain Arrêt",
        stationName: nextStop.stationName,
        time: nextStop.arrivalTime,
        pk: nextStop.pk,
        isPending: false
      };
    }
    return null;
  }, [activeMission, nextStop, currentTime]);


  // ANNONCE VOCALE DU PROCHAIN ARRÊT
  const lastAnnouncedStopPk = useRef<number | null>(null);
  useEffect(() => {
    if (role === 'driver' && nextStop && nextStop.pk !== lastAnnouncedStopPk.current) {
      // Éviter d'annoncer au tout premier chargement de la carte (sinon ça parle dès qu'on clique sur la mission)
      if (lastAnnouncedStopPk.current !== null) {
        // Le `setTimeout` évite que l'annonce se coupe ou bégaye s'il y a d'autres sons au même moment
        setTimeout(() => {
          // Désactiver le bip pour l'annonce de la prochaine gare, moins intrusif
          announce(`Prochain arrêt, ${nextStop.stationName}.`, false, false);
        }, 1000);
      }
      lastAnnouncedStopPk.current = nextStop.pk;
    }
  }, [nextStop, role]);

  // ANNONCE SONORE "ARRET DEMANDE" A 300m
  const lastRequestedStopAlertPk = useRef<number | null>(null);
  useEffect(() => {
    if (role === 'driver' && displayStatus?.pk !== undefined && telemetry.currentPK !== null) {
      const isRequested = requestedStops.some(s => s.trim().toLowerCase() === displayStatus.stationName.trim().toLowerCase());
      const dist = Math.abs(telemetry.currentPK - displayStatus.pk);

      if (isRequested && dist <= 0.300 && displayStatus.pk !== lastRequestedStopAlertPk.current) {
        lastRequestedStopAlertPk.current = displayStatus.pk;

        if (audioUnlocked && audioEnabled) {
          try {
            const audio = new Audio('Vous avez le prochaine arrêt.mp3');
            audio.play().catch(e => console.error("Erreur lecture MP3 arret demande:", e));
          } catch (e) {
            console.error("Erreur création Audio:", e);
          }
        }
      }
    }
  }, [role, displayStatus, telemetry.currentPK, requestedStops, audioUnlocked, audioEnabled]);

  // MISSION COMPLETION DETECTION REMOVED - NOW MANUAL

  // GPS PING SYSTEM (10 SECONDES)
  useEffect(() => {
    let pingInterval: any = null;

    const performPing = async () => {
      if (role === 'driver' && activeMission && currentCoords && session?.user?.id) {
        console.log(`[GPS-PING] Diffusion de la position pour mission: ${activeMission.trainNumber}`);
        try {
          await localApi.updateGpsPosition(
            session.user.id,
            currentCoords[0],
            currentCoords[1],
            `${activeMission.trainNumber} - ${activeMission.lineName}`
          );
        } catch (err) {
          console.error("[GPS-PING] Erreur d'envoi de la position:", err);
        }
      }
    };

    if (activeMission && role === 'driver') {
      // Premier ping immédiat dès qu'on a les coordonnées
      performPing();
      // Puis toutes les 10 secondes (10 000 ms)
      pingInterval = setInterval(performPing, 10000);
    }

    return () => {
      if (pingInterval) clearInterval(pingInterval);
    };
  }, [activeMission, currentCoords, role, session?.user?.id]);

  const deleteTrainPosition = async () => {
    if (session?.user?.id) {
      console.log(`[GPS-PING] Suppression de la position GPS...`);
      try {
        await localApi.deleteGpsPosition(session.user.id);
      } catch (err) {
        console.error("[GPS-PING] Erreur lors de la suppression de la position:", err);
      }
    }
  };

  const logMissionToEVS = () => {
    if (!activeMission) return;
    const isPermanent = PERMANENT_MISSIONS.some(p => p.id === activeMission.id);
    if (!isPermanent) {
      const now = new Date();
      const monthPart = `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}`;
      const monthKey = `evs_history_${monthPart}`;
      const todayISO = now.toISOString().split('T')[0];
      
      const rawHistory = localStorage.getItem(monthKey);
      const evsHistory = rawHistory ? JSON.parse(rawHistory) : {};

      if (!evsHistory[todayISO]) {
        evsHistory[todayISO] = [];
      }

      if (!evsHistory[todayISO].includes(activeMission.trainNumber)) {
        evsHistory[todayISO].push(activeMission.trainNumber);
        localStorage.setItem(monthKey, JSON.stringify(evsHistory));
        console.log(`[EVS] Mission ${activeMission.trainNumber} logged for ${todayISO} in ${monthKey}.`);
      }
    }
  };

  const handleStartMission = (m: Mission) => {
    setActiveMission(m);
    logAction(displayName, 'DÉBUT DE MISSION', `Mission TR ${m.trainNumber} - ${m.lineName} commencée`);
    logSecurityEvent('MISSION_START', { trainNumber: m.trainNumber, lineName: m.lineName });
  };

  const handleCancelMission = async () => {
    if (window.confirm("Voulez-vous annuler l'enregistrement en cours SANS noter la mission dans l'EVS ?")) {
      if (activeMission) {
        logSecurityEvent('MISSION_CANCEL', { trainNumber: activeMission.trainNumber, lineName: activeMission.lineName });
      }
      // Nettoyage DB des arrêts demandés pour ce train
      if (activeMission) {
        try {
          await localApi.deleteStopRequest(activeMission.trainNumber);
        } catch (err) {
          console.error("Erreur lors de la suppression locale des arrêts demandés :", err);
        }
      }
      await deleteTrainPosition();
      setActiveMission(null);
      setIsGpsTracking(false);
      setMissionEndedMessage(false);
    }
  };

  const handleEndMission = async () => {
    if (window.confirm("Voulez-vous valider manuellement la fin de cette mission ? Elle sera ajoutée à l'EVS.")) {
      if (activeMission) {
        logSecurityEvent('MISSION_END', { trainNumber: activeMission.trainNumber, lineName: activeMission.lineName });
      }
      // Nettoyage DB des arrêts demandés pour ce train
      if (activeMission) {
        try {
          await localApi.deleteStopRequest(activeMission.trainNumber);
        } catch (err) {
          console.error("Erreur lors de la suppression locale des arrêts demandés :", err);
        }
      }
      await deleteTrainPosition();
      logMissionToEVS();
      setActiveMission(null);
      setIsGpsTracking(false);
      setMissionEndedMessage(false);
    }
  };

  if (!authInitialized) {
    return null; // ou un spinner
  }

  if (!session) {
    return <Login onLoginSuccess={(session) => setSession(session)} />;
  }

  return (
    <>
      {isEmergencyStopRequested && (
        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-md flex items-center justify-center p-6 safe-area-inset">
          <div className="bg-red-950/95 border-[12px] border-red-600 rounded-[3rem] p-8 max-w-lg w-full text-center space-y-6 animate-pulse shadow-[0_0_50px_rgba(220,38,38,0.8)]">
            <div className="text-6xl animate-bounce">🚨</div>
            <h2 className="text-white text-3xl font-black uppercase tracking-tight">demande d'arrêt immédiat!</h2>
            <p className="text-red-300 text-xs font-bold uppercase tracking-widest">Le chef de train demande l'arrêt d'urgence de la circulation.</p>
            <button
              onClick={async () => {
                if (activeMission) {
                  await localApi.cancelEmergencyStop(activeMission.trainNumber);
                  setIsEmergencyStopRequested(false);
                }
              }}
              className="w-full py-4 bg-white text-red-600 hover:bg-slate-100 rounded-2xl font-black uppercase tracking-widest text-xs transition-all active:scale-95 shadow-lg shadow-red-900/30"
            >
              Acquitter
            </button>
          </div>
        </div>
      )}
      {role === 'station_master' ? (
        <StationMasterView 
          displayName={displayName} 
          onLogout={async () => { await deleteTrainPosition(); await handleLogout(); }} 
        />
      ) : role === 'operator_traction' ? (
        <TractionOperatorView
          displayName={displayName}
          onLogout={async () => { await deleteTrainPosition(); await handleLogout(); }}
          currentTime={currentTime}
          isMobile={isMobile}
          mapStyle={mapStyle}
          pathBalagne={pathBalagne}
          pathCentrale={pathCentrale}
          ltvList={ltvList}
          incidents={incidents}
          missions={missions}
          fetchLTVs={fetchLTVs}
          handleAddMission={handleAddMission}
          handleAddMultipleMissions={handleAddMultipleMissions}
          handleUpdateMission={handleUpdateMission}
          handleDeleteMission={handleDeleteMission}
          unreadSecuCount={unreadSecuCount}
          session={session}
        />
      ) : role === 'conductor' ? (
        <ConductorView
          activeMission={activeMission}
          missions={missions}
          setActiveMission={setActiveMission}
          currentTime={currentTime}
          displayName={displayName}
          onLogout={handleLogout}
          displayStatus={displayStatus}
          requestedStops={requestedStops}
          stopRequests={stopRequests}
          toggleRequestedStop={toggleRequestedStop}
        />
      ) : role === 'driver' && activeMission ? (
        <div key={session?.user?.id || 'logged-out'} className="h-screen bg-[#0f172a] flex flex-col overflow-hidden text-slate-300 font-sans">
          <header className="h-[calc(4.5rem+env(safe-area-inset-top))] pt-[env(safe-area-inset-top)] shrink-0 bg-[#0f172a] flex justify-between items-center px-4 border-b border-black/50 shadow-md z-[1000] relative">
            <button
              onClick={() => setIsMissionManagementOpen(!isMissionManagementOpen)}
              className="flex flex-col items-center justify-center hover:bg-white/5 px-2 py-1 rounded-xl transition-all active:scale-95 border border-transparent hover:border-white/10"
            >
              <TrainFront className="text-blue-500 w-8 h-8 mb-0.5" strokeWidth={1.5} />
              <span className="text-[8px] font-black text-slate-400 tracking-widest uppercase mt-[-2px]">MISSION: {activeMission.trainNumber}</span>
            </button>
            <div className="flex-1 flex flex-col items-center justify-center absolute left-1/2 -translate-x-1/2">
              {role === 'driver' && activeMission && displayStatus && displayStatus.time ? (() => {
                const [hours, minutes] = displayStatus.time.split(':').map(Number);
                const expectedTime = new Date();
                expectedTime.setHours(hours, minutes, 0, 0);

                const diffMs = currentTime.getTime() - expectedTime.getTime();
                const diffMins = Math.round(diffMs / (1000 * 60));

                let timeColor = 'text-[#f8fafc]';
                if (diffMins > 3) timeColor = 'text-red-500';
                else if (diffMins < -3) timeColor = 'text-yellow-400';
                else if (Math.abs(diffMins) <= 3) timeColor = 'text-green-500';

                return (
                  <>
                    <span className={`text-[1.3rem] font-bold tracking-widest ${timeColor} drop-shadow-md pb-0.5`}>
                      {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </span>
                    {diffMins > 3 && (
                      <span className="text-red-500 text-[9px] font-black uppercase tracking-widest animate-pulse leading-none mt-[-2px]">
                        Retard : +{diffMins} min
                      </span>
                    )}
                  </>
                );
              })() : (
                <span className="text-[1.3rem] font-bold tracking-widest text-[#f8fafc] drop-shadow-md pb-1">
                  {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              )}
            </div>
            <button onClick={handleLogout} className="flex items-center justify-center p-2 hover:bg-red-500/10 rounded-xl transition-all active:scale-95 group">
              <PowerIcon className="w-7 h-7 text-red-500 group-hover:scale-110 transition-transform drop-shadow-[0_0_8px_rgba(239,68,68,0.4)]" />
            </button>
          </header>

          <main className="flex-1 flex flex-col relative overflow-hidden bg-[#111827]">
            {/* MODAL DE GESTION DE MISSION (HEADER SHORTCUT) */}
            {isMissionManagementOpen && (
              <div className="absolute top-2 left-4 w-72 glass-panel-heavy rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.6)] overflow-hidden pointer-events-auto border border-white/20 z-[3000] animate-in slide-in-from-top-4 fade-in duration-300">
                <div className="p-4 border-b border-white/5 bg-blue-500/5">
                  <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest text-center">Gestion de Mission</p>
                  <p className="text-[9px] text-slate-500 text-center font-bold mt-1">TR {activeMission.trainNumber} • {activeMission.lineName}</p>
                </div>
                <div className="p-3 space-y-3">
                  {/* Boutons d'action principaux */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => { setIsMissionManagementOpen(false); handleEndMission(); }}
                      className="flex flex-col items-center justify-center p-3 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 rounded-2xl transition-all active:scale-95 group text-center"
                    >
                      <Check size={16} className="mb-1" />
                      <span className="text-[9px] font-black uppercase tracking-wider">Finir</span>
                    </button>

                    <button
                      onClick={() => { setIsMissionManagementOpen(false); handleCancelMission(); }}
                      className="flex flex-col items-center justify-center p-3 bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20 rounded-2xl transition-all active:scale-95 group text-center"
                    >
                      <X size={16} className="mb-1" />
                      <span className="text-[9px] font-black uppercase tracking-wider">Annuler</span>
                    </button>
                  </div>

                  <div className="w-full h-[1px] bg-white/5" />

                  {/* Dispositif de recherche / changement rapide de mission */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">Changer de Mission</label>
                      {activeMission && (
                        <button
                          onClick={() => { setIsMissionManagementOpen(false); setActiveMission(null); }}
                          className="text-[9px] text-cyan-400 hover:text-cyan-300 font-bold uppercase tracking-wider"
                        >
                          Quitter
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="N° Train ou Destination..."
                        value={quickSearchQuery}
                        onChange={(e) => setQuickSearchQuery(e.target.value)}
                        className="w-full bg-slate-950/60 border border-white/10 focus:border-cyan-500/50 rounded-xl py-2 pl-8 pr-3 text-xs text-white placeholder-slate-600 outline-none transition-all font-medium"
                      />
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                    </div>

                    <div className="max-h-40 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                      {missions
                        .filter(m => 
                          m.id !== activeMission.id &&
                          (m.trainNumber.toLowerCase().includes(quickSearchQuery.toLowerCase()) ||
                           m.lineName.toLowerCase().includes(quickSearchQuery.toLowerCase()))
                        )
                        .slice(0, 5)
                        .map(m => (
                          <button
                            key={m.id}
                            onClick={() => {
                              handleStartMission(m);
                              setIsMissionManagementOpen(false);
                              setQuickSearchQuery('');
                            }}
                            className="w-full flex items-center justify-between p-2.5 bg-slate-800/30 hover:bg-slate-700/50 border border-white/5 rounded-xl text-left transition-all active:scale-[0.98] group"
                          >
                            <span className="text-cyan-400 font-bold text-xs font-mono">{m.trainNumber}</span>
                            <span className="text-white text-[10px] truncate max-w-[140px] font-medium ml-2 flex-1">{m.lineName}</span>
                            <span className="text-slate-500 group-hover:text-cyan-400 text-xs font-bold">&rarr;</span>
                          </button>
                        ))
                      }
                      {missions.filter(m => 
                        m.id !== activeMission.id &&
                        (m.trainNumber.toLowerCase().includes(quickSearchQuery.toLowerCase()) ||
                         m.lineName.toLowerCase().includes(quickSearchQuery.toLowerCase()))
                      ).length === 0 && (
                        <p className="text-[10px] text-slate-600 text-center py-2 font-medium italic">Aucune mission trouvée</p>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setIsMissionManagementOpen(false)}
                  className="w-full py-3 bg-black/20 text-slate-500 text-[9px] font-black uppercase tracking-widest border-t border-white/5 hover:text-white transition-colors"
                >
                  Fermer
                </button>
              </div>
            )}

            {isEvsPageOpen ? (
              <EvsView driverName={displayName} />
            ) : (
              <>
                <div className={`absolute top-6 left-4 right-4 z-[1000] rounded-[1.2rem] p-5 flex justify-between items-center shadow-[0_10px_40px_rgba(0,0,0,0.5)] border transition-all duration-500
                  ${requestedStops.some(s => s.trim().toLowerCase() === (displayStatus?.stationName || '').trim().toLowerCase()) &&
                    displayStatus?.pk !== undefined && telemetry.currentPK !== null && Math.abs(telemetry.currentPK - displayStatus.pk) <= 0.300
                    ? 'bg-blue-500 border-blue-400 animate-pulse shadow-[0_0_50px_rgba(59,130,246,0.6)]'
                    : 'bg-[#141b2e]/95 backdrop-blur-md border-white/5'}`}>
                  <div className="flex-[2] border-r border-white/10 overflow-hidden">
                    <p className={`text-[10px] font-black uppercase tracking-widest mb-1.5 opacity-80 ${requestedStops.some(s => s.trim().toLowerCase() === (displayStatus?.stationName || '').trim().toLowerCase()) && displayStatus?.pk !== undefined && telemetry.currentPK !== null && Math.abs(telemetry.currentPK - displayStatus.pk) <= 0.300 ? 'text-blue-100' : 'text-slate-400'}`}>
                      {requestedStops.some(s => s.trim().toLowerCase() === (displayStatus?.stationName || '').trim().toLowerCase()) && displayStatus?.pk !== undefined && telemetry.currentPK !== null && Math.abs(telemetry.currentPK - displayStatus.pk) <= 0.300 ? 'Arrêt Demandé' : 'Prochain arrêt'}
                    </p>
                    <p className="text-white font-black text-[1.1rem] uppercase truncate leading-none mb-1">{displayStatus?.stationName || 'EN LIGNE'}</p>
                    <p className={`${requestedStops.some(s => s.trim().toLowerCase() === (displayStatus?.stationName || '').trim().toLowerCase()) && displayStatus?.pk !== undefined && telemetry.currentPK !== null && Math.abs(telemetry.currentPK - displayStatus.pk) <= 0.300 ? 'text-white' : 'text-[#3b82f6]'} text-[13px] font-black flex items-center gap-2`}>
                      {displayStatus?.time || '--:--'} <span className={`${requestedStops.some(s => s.trim().toLowerCase() === (displayStatus?.stationName || '').trim().toLowerCase()) && displayStatus?.pk !== undefined && telemetry.currentPK !== null && Math.abs(telemetry.currentPK - displayStatus.pk) <= 0.300 ? 'text-white' : 'text-blue-500/80'} text-[10px] pt-0.5`}>▲</span>
                    </p>
                  </div>
                  <div className="flex-1 text-center border-r border-white/10 px-2 min-w-[70px]">
                    <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mb-1.5 opacity-80">PK</p>
                    <p className="text-white font-black text-[1.4rem] leading-none">{telemetry.currentPK !== null ? telemetry.currentPK.toFixed(3) : '---'}</p>
                  </div>
                  <div className="flex-1 text-right pl-2 min-w-[80px]">
                    <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mb-1.5 opacity-80">Vitesse</p>
                    <p className="text-white font-black text-[1.4rem] leading-none">{isSimulationMode ? '80' : '---'}&nbsp;<span className="text-[10px] font-bold text-slate-400 lowercase opacity-80">km/h</span></p>
                  </div>
                </div>


                <div className="absolute inset-0 z-0 bg-[#0f172a] border-t border-white/5">
                  <MapContainer center={pathBalagne[0] || [42.56, 8.75]} zoom={15} style={{ height: '100%', width: '100%' }} zoomControl={false} className="driver-map">
                    <MapResizer />
                    <AutoCenterOnMissionStart activeMission={activeMission} />
                    <RecenterMapHandler currentCoords={currentCoords} isGpsTracking={isGpsTracking} setIsGpsTracking={setIsGpsTracking} recenterTrigger={recenterTrigger} />

                    {mapStyle === 'satellite' ? (
                      <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                    ) : (
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                    )}
                    <Polyline positions={pathBalagne} color="#22d3ee" weight={8} opacity={0.6} />
                    <Polyline positions={pathCentrale} color="#a855f7" weight={8} opacity={0.6} />
                    <PKMarkers />
                    <StationsMapLayer />
                    <LTVMapLayer ltvList={ltvList} />
                    <IncidentsLayer incidents={incidents} />
                    <TrainPositionsLayer currentUserId={session?.user?.id} />
                    {currentCoords && (
                      <Marker position={currentCoords} icon={L.divIcon({
                        className: 'd-marker',
                        iconSize: [48, 48],
                        iconAnchor: [24, 24],
                        html: `<div class="w-12 h-12 bg-cyan-500/30 rounded-full border-4 border-white shadow-[0_0_40px_rgba(6,182,212,0.8)] flex items-center justify-center animate-pulse"><div class="w-4 h-4 bg-white rounded-full shadow-[0_0_10px_white]"></div></div>`
                      })} />
                    )}
                  </MapContainer>
                </div>

                {/* ZONE D'ACTIONS FLOTTANTES ALIGNÉE À DROITE SANS CHEVAUCHER LA BARRE DE TABS */}
                <div
                  style={{
                    position: 'absolute',
                    right: '16px',
                    bottom: 'calc(6.5rem + env(safe-area-inset-bottom))',
                    width: '53px',
                    zIndex: 4000
                  }}
                  className="flex flex-col items-center gap-3 pointer-events-none"
                >
                  <div className="pointer-events-auto space-y-3">
                    {currentCoords && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const newTrackingState = !isGpsTracking;
                          setIsGpsTracking(newTrackingState);
                          if (newTrackingState) {
                            setRecenterTrigger(prev => prev + 1);
                          }
                        }}
                        className={`w-[50px] h-[50px] rounded-2xl flex items-center justify-center transition-all glass-panel
                            ${isGpsTracking ? 'bg-cyan-500/20 border-cyan-400 text-cyan-300 shadow-[0_0_20px_rgba(6,182,212,0.4)]' : 'text-slate-400 hover:text-white'} `}
                        title="Recadrer sur ma position"
                      >
                        {isGpsTracking ? <Navigation size={24} className="text-cyan-400 fill-cyan-400" /> : <LocateFixed size={24} />}
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEndMission(); }}
                      className="w-[50px] h-[50px] rounded-2xl flex items-center justify-center transition-all glass-panel bg-emerald-500/20 border-emerald-400 text-emerald-300 shadow-[0_0_20px_rgba(16,185,129,0.4)] hover:bg-emerald-500/40"
                      title="Fin de mission"
                    >
                      <Check size={24} />
                    </button>
                  </div>

                  <div className="pointer-events-auto">
                    <button
                      onClick={() => { setIsPhoneDirectoryOpen(!isPhoneDirectoryOpen); setIsSettingsOpen(false); }}
                      className={`w-[50px] h-[50px] rounded-2xl flex items-center justify-center transition-all shadow-2xl border ${isPhoneDirectoryOpen ? 'bg-cyan-500 border-cyan-400 text-slate-900 shadow-[0_0_20px_rgba(6,182,212,0.6)]' : 'bg-slate-800/90 backdrop-blur-md border-white/10 text-slate-400 hover:text-white hover:bg-slate-700/90'}`}
                    >
                      <Phone size={24} className={isPhoneDirectoryOpen ? 'transform scale-110 transition-transform duration-300' : 'transition-transform duration-300'} />
                    </button>
                  </div>

                  <div className="pointer-events-auto">
                    <IncidentManager
                      onAdd={async (type, pk, desc) => {
                        const newIncident: Incident = { id: Date.now().toString(), type: type as any, pk, timestamp: Date.now(), description: desc };
                        try {
                          await localApi.reportIncident(newIncident);
                          fetchIncidents();
                        } catch (err) {
                          console.error("Erreur envoi incident:", err);
                        }
                        logAction(displayName, ' SIGNALEMENT', `Incident ${type} signalé au PK ${pk.toFixed(3)} : ${desc}`);
                        logSecurityEvent('INCIDENT_REPORT', { type, pk, description: desc });
                      }}
                      currentPK={telemetry.currentPK}
                      role={role}
                      isMobile={isMobile}
                      isEmbedded={true}
                    />
                  </div>
                </div>


                {activeVisualAlert && (
                  <div className="absolute inset-x-0 inset-y-0 z-[2000] pointer-events-none flex items-center justify-center bg-red-600/10">
                    <div className="bg-slate-900 border-8 border-red-600 p-8 sm:p-12 rounded-3xl shadow-[0_0_100px_rgba(220,38,38,0.8)] flex flex-col items-center justify-center max-w-[90%] pointer-events-auto transform animate-in fade-in zoom-in duration-300">
                      <div className="flex items-center gap-4 mb-4">
                        <span className="text-4xl md:text-6xl animate-bounce">⚠️</span>
                        <h2 className="text-3xl md:text-5xl font-black uppercase tracking-widest text-red-500 text-center animate-pulse">Ralentissement</h2>
                        <span className="text-4xl md:text-6xl animate-bounce">⚠️</span>
                      </div>
                      <div className="bg-red-600 text-white font-extrabold text-[6rem] md:text-[10rem] leading-none px-12 py-4 rounded-full border-8 border-slate-900 shadow-2xl my-6 animate-pulse">{activeVisualAlert.speed}</div>
                      <p className="text-xl md:text-3xl font-bold uppercase tracking-widest text-white text-center">{activeVisualAlert.reason || "Limitation Temporaire"}</p>
                    </div>
                  </div>
                )}

                {missionEndedMessage && (
                  <div className="absolute inset-x-0 inset-y-0 z-[3000] flex items-center justify-center bg-black/80 backdrop-blur-md">
                    <div className="bg-slate-900 border-2 border-cyan-500 p-8 sm:p-12 rounded-3xl shadow-[0_0_50px_rgba(6,182,212,0.5)] flex flex-col items-center max-w-lg mx-4 text-center animate-in fade-in zoom-in duration-500">
                      <div className="w-24 h-24 bg-cyan-500/20 rounded-full flex items-center justify-center mb-6"><span className="text-6xl">🏁</span></div>
                      <h2 className="text-3xl text-white font-black uppercase italic mb-2">Mission Accomplie</h2>
                      <button onClick={() => { setMissionEndedMessage(false); setIsGpsTracking(false); setActiveMission(null); }} className="w-full mt-8 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-black uppercase tracking-widest py-4 rounded-xl transition-all">Nouvelle Mission</button>
                    </div>
                  </div>
                )}

                {incidentToValidate && <IncidentValidator incident={incidentToValidate} onResolve={handleResolveIncident} />}

                {isAccountPageOpen && session && (
                  <div className="absolute inset-0 z-[4000] bg-[#020617] flex flex-col p-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                    <header className="flex justify-between items-center mb-8">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-cyan-500/10 rounded-2xl flex items-center justify-center border border-cyan-500/20">
                          <Shield className="text-cyan-500" size={24} />
                        </div>
                        <div>
                          <h2 className="text-xl font-black uppercase tracking-tight text-white">Mon Compte</h2>
                          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">Gestion des données personnelles</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => setIsAccountPageOpen(false)}
                        className="p-3 bg-slate-800 hover:bg-slate-700 rounded-2xl text-slate-400 border border-white/5 transition-all"
                      >
                        <X size={20} />
                      </button>
                    </header>

                    <div className="flex-1 space-y-6 max-w-md mx-auto w-full">
                      <div className="bg-slate-900/50 border border-white/5 rounded-3xl p-6 space-y-4">
                        <div className="space-y-1">
                          <p className="text-[10px] text-cyan-500 font-black uppercase tracking-widest">Adresse E-mail</p>
                          <p className="text-white font-bold">{session.user.email}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-[10px] text-cyan-500 font-black uppercase tracking-widest">Rôle Opérationnel</p>
                          <span className="px-2 py-1 bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 rounded text-[9px] font-black uppercase tracking-widest">
                            {role || 'driver'}
                          </span>
                        </div>
                        <div className="space-y-1">
                          <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest">Identifiant Unique</p>
                          <p className="text-[10px] font-mono text-slate-600 truncate">{session.user.id}</p>
                        </div>
                      </div>

                      <div className="pt-8 border-t border-white/5">
                        <h3 className="text-red-500 font-black uppercase tracking-widest text-xs mb-4">Zone de Danger</h3>
                        <div className="bg-red-500/5 border border-red-500/20 rounded-3xl p-6 space-y-4">
                          <p className="text-xs text-slate-400 leading-relaxed">
                            Conformément au RGPD, vous disposez du droit à l'effacement de vos données. 
                            La suppression de votre compte est **définitive** et entraînera la perte de tous vos accès.
                          </p>
                          <button 
                            onClick={async () => {
                              const confirm1 = window.confirm("ATTENTION : Cette action est irréversible. Souhaitez-vous vraiment supprimer définitivement votre compte ASTA RAIL ?");
                              if (!confirm1) return;
                              
                              const confirm2 = window.prompt("Pour confirmer, veuillez saisir votre adresse e-mail :");
                              if (confirm2 !== session.user.email) {
                                if (confirm2 !== null) alert("L'adresse e-mail saisie ne correspond pas.");
                                return;
                              }

                              try {
                                const { data, error } = await supabase.functions.invoke('admin-users', {
                                  body: { 
                                    action: 'delete',
                                    payload: { user_id: session.user.id }
                                  }
                                });

                                if (error || (data && !data.success)) {
                                  throw new Error(error?.message || data?.error || "Erreur lors de la suppression");
                                }

                                alert("Votre compte a été supprimé avec succès. Vous allez être déconnecté.");
                                await localApi.logout();
                                window.location.reload();
                              } catch (err: any) {
                                alert(`Erreur : ${err.message}`);
                              }
                            }}
                            className="w-full py-4 bg-red-600 hover:bg-red-500 text-white font-black uppercase tracking-[0.2em] text-[10px] rounded-2xl transition-all active:scale-95 shadow-lg shadow-red-900/20"
                          >
                            Supprimer mon compte
                          </button>
                        </div>
                      </div>
                    </div>

                    <footer className="mt-auto pt-6 text-center">
                      <p className="text-[9px] font-bold text-slate-600 uppercase tracking-widest italic">
                        ASTA RAIL • Protection des Données Certifiée
                      </p>
                    </footer>
                  </div>
                )}

                {isSettingsOpen && (
                  <div className="absolute bottom-[2rem] right-[2rem] w-64 glass-panel-heavy rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden pointer-events-auto border border-white/20 z-[2000] animate-in slide-in-from-right-4 fade-in duration-200">
                    <div className="p-3 border-b border-white/5 space-y-2">
                      <button onClick={() => { setIsAccountPageOpen(true); setIsSettingsOpen(false); }} className="w-full flex items-center gap-3 p-4 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/20 rounded-2xl transition-all active:scale-95">
                        <Shield size={18} className="text-cyan-500" />
                        <span className="text-[11px] font-black uppercase tracking-widest mt-0.5">Mon Compte</span>
                      </button>
                    </div>
                    <div className="p-3 space-y-2">
                      <button onClick={() => { if (!audioUnlocked) { unlockAudio(); setAudioEnabled(true); } else { setAudioEnabled(prev => { if (!prev) playActivationBeep(); return !prev; }); } }} className={`w-full flex items-center justify-between p-3.5 rounded-2xl transition-all active:scale-95 ${(!audioUnlocked || !audioEnabled) ? 'bg-red-500/10 text-red-500 border border-red-500/20' : 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'}`}><span className="text-[10px] font-black uppercase">{!audioUnlocked ? 'Activer Audio' : (audioEnabled ? 'Audio ON' : 'Audio OFF')}</span>{(!audioUnlocked || !audioEnabled) ? <VolumeX size={18} /> : <Volume2 size={18} />}</button>
                      <button 
                        onClick={() => {
                          setMapStyle(prev => prev === 'osm' ? 'satellite' : 'osm');
                          // Forcer le map.invalidateSize après transition de style de carte
                          setTimeout(() => { window.dispatchEvent(new Event('resize')); }, 200);
                        }} 
                        className="w-full flex items-center justify-between p-3.5 rounded-2xl bg-slate-800/60 hover:bg-slate-700/60 text-slate-300 border border-white/5 transition-all active:scale-95"
                      >
                        <span className="text-[10px] font-black uppercase">Carte : {mapStyle === 'osm' ? 'Plan (léger)' : 'Satellite'}</span>
                        <Navigation size={14} className={mapStyle === 'satellite' ? 'text-cyan-400' : 'text-slate-400'} />
                      </button>
                    </div>
                  </div>
                )}

                {isPhoneDirectoryOpen && (
                  <div className="absolute bottom-[10.5rem] right-[5.5rem] w-[18rem] bg-[#1a2030]/95 backdrop-blur-lg border border-blue-500/30 rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden pointer-events-auto z-[2000] animate-in slide-in-from-right-4 fade-in duration-200">
                    <div className="bg-gradient-to-r from-blue-600/20 to-transparent p-4 border-b border-blue-500/20 flex justify-between items-center"><div className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center border border-blue-500/30 text-blue-400"><Phone size={16} /></div><h3 className="text-white font-black uppercase tracking-widest text-sm">Répertoire</h3></div><button onClick={() => setIsPhoneDirectoryOpen(false)} className="text-slate-400 hover:text-white p-1">✕</button></div>
                    <div className="p-3 space-y-2 max-h-[60vh] overflow-y-auto">
                      <div className="p-3 bg-slate-800/40 hover:bg-slate-700/60 transition-colors rounded-xl border border-white/5 flex justify-between items-center cursor-pointer group"><div><p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-0.5 group-hover:text-blue-400 transition-colors">Urgence Voie / Régulation</p><p className="text-white font-mono font-black text-[13px]">PCC Casamozza</p></div><Phone size={18} className="text-blue-500 group-hover:text-blue-400 transition-colors" /></div>
                      <div className="p-3 bg-slate-800/40 hover:bg-slate-700/60 transition-colors rounded-xl border border-white/5 flex justify-between items-center cursor-pointer group"><div><p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-0.5 group-hover:text-blue-400 transition-all">Assistance Matériel</p><p className="text-white font-mono font-black text-[13px]">Dépôt SCW</p></div><Phone size={18} className="text-blue-500 group-hover:text-blue-400 transition-colors" /></div>
                      <div className="p-3 bg-slate-800/40 hover:bg-slate-700/60 transition-colors rounded-xl border border-white/5 flex justify-between items-center cursor-pointer group"><div><p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-0.5 group-hover:text-blue-400 transition-all">Services Secours</p><p className="text-white font-mono font-black text-[13px]">Pompiers / SAMU</p></div><Phone size={18} className="text-blue-500 group-hover:text-blue-400 transition-colors" /></div>
                    </div>
                  </div>
                )}
              </>
            )}
          </main>

          {/* Navigation bottom bar fixed and styled according to mock */}
          <nav className="h-[calc(6rem+env(safe-area-inset-bottom))] pb-[calc(1rem+env(safe-area-inset-bottom))] shrink-0 bg-[#161f31] flex justify-around items-center z-[2000] border-t border-black/30 w-full relative">
            <button
              onClick={() => { setIsAccountPageOpen(false); setIsSecuDashboardOpen(true); setIsStopRequestsPageOpen(false); setIsEvsPageOpen(false); setIsPhoneDirectoryOpen(false); setIsSettingsOpen(false); }}
              className={`flex flex-col items-center justify-center w-16 h-16 transition-all gap-1 opacity-60 hover:opacity-100 flex-1 ${(!isAccountPageOpen && !isSecuDashboardOpen && !isEvsPageOpen && !isSettingsOpen) ? 'text-white' : 'text-slate-400'}`}
            >
              <Shield className="w-6 h-6 mb-0.5" strokeWidth={1.5} />
              <span className="text-[9px] font-black tracking-widest uppercase mt-0.5">SECU</span>
            </button>
            <button onClick={() => { setIsAccountPageOpen(false); setIsStopRequestsPageOpen(false); setIsEvsPageOpen(false); setIsPhoneDirectoryOpen(false); setIsSettingsOpen(false); }} className="flex flex-col items-center justify-center w-16 h-16 text-slate-400 hover:text-white transition-all gap-1 opacity-60 hover:opacity-100 flex-1">
              <AlertTriangle className="w-6 h-6 mb-0.5" strokeWidth={1.5} />
              <span className="text-[9px] font-black tracking-widest uppercase mt-0.5">ALERTES</span>
            </button>
            <button
              onClick={() => { setIsAccountPageOpen(false); console.log("[DEBUG] Opening RequestedStopsView (Block 1)"); setIsStopRequestsPageOpen(true); setIsEvsPageOpen(false); setIsSettingsOpen(false); setIsPhoneDirectoryOpen(false); }}
              className={`flex flex-col items-center justify-center w-16 h-16 transition-all gap-1 flex-1 relative top-[-1px]
                ${requestedStops.some(s => s.trim().toLowerCase() === (displayStatus?.stationName || '').trim().toLowerCase())
                  ? 'text-blue-500 opacity-100 animate-pulse drop-shadow-[0_0_8px_rgba(59,130,246,0.6)]'
                  : 'text-slate-400 opacity-60 hover:opacity-100 hover:text-white'}`}>
              <Hand className={`w-6 h-6 mb-0.5 ${requestedStops.some(s => s.trim().toLowerCase() === (displayStatus?.stationName || '').trim().toLowerCase()) ? 'fill-blue-500/20' : ''}`} strokeWidth={1.5} />
              <span className="text-[8px] font-black tracking-[0.03em] uppercase text-center leading-[1.2] px-1 mt-0.5">ARRÊT<br />DEMANDÉ</span>
            </button>
            <button onClick={() => { setIsAccountPageOpen(false); setIsStopRequestsPageOpen(false); setIsEvsPageOpen(!isEvsPageOpen); setIsSettingsOpen(false); setIsPhoneDirectoryOpen(false); }} className={`flex flex-col items-center justify-center w-16 h-16 transition-all gap-1 flex-1 ${isEvsPageOpen ? 'text-[#10b981] opacity-100' : 'text-slate-400 opacity-60 hover:opacity-100 hover:text-white'}`}>
              <FileSpreadsheet className="w-6 h-6 mb-0.5" strokeWidth={1.5} />
              <span className="text-[9px] font-black tracking-widest uppercase mt-0.5">EVS</span>
            </button>
            <button onClick={() => { setIsAccountPageOpen(false); setIsStopRequestsPageOpen(false); setIsSettingsOpen(!isSettingsOpen); setIsPhoneDirectoryOpen(false); setIsEvsPageOpen(false); }} className={`flex flex-col items-center justify-center w-16 h-16 transition-all gap-1 flex-1 ${isSettingsOpen ? 'text-[#3b82f6] opacity-100' : 'text-slate-400 opacity-60 hover:opacity-100 hover:text-white'}`}>
              <Settings className="w-6 h-6 mb-0.5" strokeWidth={1.5} />
              <span className="text-[9px] font-black tracking-widest uppercase mt-0.5">RÉGLAGES</span>
            </button>
          </nav>

          {/* PAGE DÉTAIL ARRÊTS (Block 1) */}

        </div>
      ) : (
        <div key={session?.user?.id || 'logged-out'} className="h-screen bg-[#020617] flex flex-col overflow-hidden text-slate-300 font-sans">
          <header className={`px-4 pt-[env(safe-area-inset-top)] border-b border-white/5 bg-slate-900/90 backdrop-blur-md flex justify-between items-center z-[1000] shadow-xl ${isMobile ? 'h-[calc(4rem+env(safe-area-inset-top))]' : 'h-20'}`}>
            {/* SECTION GAUCHE : Logo & Mission */}
            <div className="flex-1 flex items-center gap-2 sm:gap-4 overflow-hidden">
              <img src="logo.png" alt="mini logo" className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} object-contain`} />
              {!isMobile && <div className="h-6 w-[1px] bg-white/10"></div>}
              <div className="flex flex-col overflow-hidden">
                <h2 className="text-white font-black italic text-[10px] sm:text-xs tracking-tighter uppercase truncate leading-none">
                  {isMobile ? displayName : `OPERATIONS ${role === 'driver' ? displayName : 'ADMIN'}`}
                  {!isMobile && <span className="ml-2 px-1.5 py-0.5 bg-cyan-500/20 text-cyan-500 text-[8px] rounded border border-cyan-500/30 animate-pulse">LIVE</span>}
                  <span className="ml-1.5 px-1 py-0.5 bg-blue-500/20 text-blue-400 text-[8px] font-mono rounded border border-blue-500/30">v2.4</span>
                </h2>
                {role === 'driver' && activeMission && (
                  <span className="text-[9px] font-mono text-cyan-400 truncate tracking-tight mt-0.5">
                    {isMobile ? `TR ${activeMission.trainNumber}` : `MISSION ${activeMission.trainNumber} : ${activeMission.lineName}`}
                  </span>
                )}
              </div>
            </div>

            {/* SECTION CENTRALE : Date & Heure (Parfaitement centrée) */}
            {!isMobile && (
              <div className="flex-1 flex flex-col items-center justify-center text-center">
                <p className="text-[10px] font-black text-cyan-500 uppercase tracking-widest leading-none mb-1">
                  {currentTime.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
                {role === 'driver' && activeMission && displayStatus && displayStatus.time ? (() => {
                  const [hours, minutes] = displayStatus.time.split(':').map(Number);
                  const expectedTime = new Date();
                  expectedTime.setHours(hours, minutes, 0, 0);

                  const diffMs = currentTime.getTime() - expectedTime.getTime();
                  const diffMins = diffMs / (1000 * 60);

                  let timeColor = 'text-green-500';
                  if (diffMins > 3) timeColor = 'text-red-500';
                  else if (diffMins < -3) timeColor = 'text-yellow-400';

                  return (
                    <div className="flex flex-col items-center">
                      <p className={`text-xl font-mono font-bold ${timeColor} leading-none drop-shadow-md`}>
                        {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </p>
                      <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                        PRÉVU: <span className="text-white">{displayStatus.time}</span>
                      </p>
                    </div>
                  );
                })() : (
                  <p className="text-xl font-mono font-bold text-white leading-none">
                    {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </p>
                )}
              </div>
            )}

            {/* HORLOGE ET STATUT HORAIRE SUR MOBILE DANS LE HEADER */}
            {isMobile && (
              <div className="flex-none flex flex-col items-center justify-center text-center px-2">
                {role === 'driver' && activeMission && displayStatus && displayStatus.time ? (() => {
                  const [hours, minutes] = displayStatus.time.split(':').map(Number);
                  const expectedTime = new Date();
                  expectedTime.setHours(hours, minutes, 0, 0);

                  const diffMs = currentTime.getTime() - expectedTime.getTime();
                  const diffMins = diffMs / (1000 * 60);

                  let timeColor = 'text-green-500'; // À l'heure (+/- 3 min)
                  if (diffMins > 3) timeColor = 'text-red-500'; // En retard
                  else if (diffMins < -3) timeColor = 'text-yellow-400'; // En avance

                  return (
                    <>
                      <p className={`text-xl font-mono font-black ${timeColor} leading-none tracking-tighter drop-shadow-md`}>
                        {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                      <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
                        PRÉVU: <span className="text-white">{displayStatus.time}</span>
                      </p>
                    </>
                  );
                })() : (
                  <p className="text-lg font-mono font-bold text-white leading-none">
                    {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
              </div>
            )}

            {/* SECTION DROITE : Actions & Status */}
            <div className="flex-1 flex items-center justify-end gap-2 sm:gap-6">
              <div className="flex items-center gap-2 sm:gap-4">

                {/* BOUTON CARNET EVS SUPPRIMÉ D'ICI - DÉPLACÉ EN FLOATING ACTION BUTTON */}



                {role === 'driver' && !activeMission && (
                  <div className="flex items-center gap-2">
                    <select onChange={(e) => setActiveMission(missions.find(m => m.id === e.target.value) || null)} className="bg-slate-800 border border-white/10 text-[9px] sm:text-[10px] text-white p-2 rounded uppercase font-bold outline-none focus:border-cyan-500 max-w-[120px] sm:max-w-none">
                      <option value="">Sélectionner une Mission</option>
                      {missions.map(m => <option key={m.id} value={m.id}>TR {m.trainNumber}</option>)}
                    </select>
                  </div>
                )}



                {role === 'driver' && displayStatus && telemetry.currentPK && !isMobile && (
                  <div className="hidden md:flex items-center gap-4 bg-slate-800/50 px-4 py-2 rounded-lg border border-white/5">
                    <div className="text-right">
                      <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">{displayStatus.label}</p>
                      <p className="text-white font-bold text-xs uppercase text-cyan-400">{displayStatus.stationName}</p>
                    </div>
                    <div className="text-right border-l border-white/10 pl-4">
                      <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Horaire</p>
                      <p className="text-white font-mono text-xs">{displayStatus.time}</p>
                    </div>
                    {!displayStatus.isPending && (
                      <div className="text-right border-l border-white/10 pl-4">
                        <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Distance</p>
                        <p className="text-white font-mono text-xs">{(Math.abs(telemetry.currentPK - displayStatus.pk)).toFixed(3)} km</p>
                      </div>
                    )}
                  </div>
                )}

                {!isMobile && (
                  <div className="hidden sm:flex items-center gap-2 border-l border-white/10 pl-6 pr-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-[10px] font-mono text-slate-400 uppercase">Satellite Linked</span>
                  </div>
                )}

                <button onClick={handleLogout} className="flex items-center justify-center p-2 hover:bg-red-500/10 rounded-xl transition-all active:scale-95 group">
                  <PowerIcon className="w-7 h-7 text-red-500 group-hover:scale-110 transition-transform drop-shadow-[0_0_8px_rgba(239,68,68,0.4)]" />
                </button>
              </div>
            </div>
          </header>

          <main className="flex-1 flex flex-col relative overflow-hidden bg-[#111827]">
            {role === 'driver' ? (
              isEvsPageOpen ? (
                <EvsView driverName={displayName} />
              ) : (
                <div className="flex-1 relative">
                  <MissionSelection missions={missions} onSelect={(m) => handleStartMission(m)} />
                  {/* Phone floating button for selection screen */}
                  <div className="absolute bottom-6 right-6 z-[1000]">
                    <button
                      onClick={() => setIsPhoneDirectoryOpen(!isPhoneDirectoryOpen)}
                      className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all shadow-2xl border ${isPhoneDirectoryOpen ? 'bg-cyan-500 border-cyan-400 text-slate-900 shadow-[0_0_20px_rgba(6,182,212,0.6)]' : 'bg-slate-800/90 backdrop-blur-md border-white/10 text-slate-400 hover:text-white'}`}
                    >
                      <Phone size={24} />
                    </button>
                    {isPhoneDirectoryOpen && (
                      <div className="absolute bottom-20 right-0 w-[18rem] bg-slate-900/95 backdrop-blur-lg border border-cyan-500/30 rounded-[2rem] shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200">
                        <div className="p-4 border-b border-cyan-500/20 flex justify-between items-center font-black uppercase text-xs tracking-widest text-white">
                          <span>Répertoire</span>
                          <button onClick={() => setIsPhoneDirectoryOpen(false)}>✕</button>
                        </div>
                        <div className="p-3 space-y-2 max-h-[50vh] overflow-y-auto">
                          <div className="p-3 bg-slate-800/60 rounded-xl border border-white/5 flex justify-between items-center cursor-pointer group hover:bg-slate-700/80">
                            <div><p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-0.5">PCC Casamozza</p><p className="text-white font-mono font-black text-[13px]">Urgence / Régulation</p></div>
                            <Phone size={18} className="text-cyan-600 group-hover:text-cyan-400" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            ) : (
              <div className="flex-1 flex overflow-hidden bg-[#0a0f1c]">
                {/* ADMIN SIDEBAR */}
                <div className="w-[80px] lg:w-[280px] shrink-0 bg-[#0f172a] border-r border-white/10 flex flex-col z-10 shadow-2xl transition-all duration-300">
                  <div className="p-4 lg:p-6 border-b border-white/10 flex flex-col items-center lg:items-start">
                    <h2 className="text-white font-black uppercase tracking-widest text-[10px] lg:text-lg text-center lg:text-left">
                      <span className="lg:hidden">PCC</span>
                      <span className="hidden lg:inline">Menu Principal</span>
                    </h2>
                    <p className="text-cyan-500 text-[9px] font-mono mt-1 pr-4 hidden lg:block">Sélectionnez une fonctionnalité</p>
                  </div>
                  
                  <nav className="flex-1 overflow-y-auto p-2 lg:p-4 flex flex-col gap-2">
                    <button onClick={() => setAdminTab('live')} title="Suivi Trafic Live" className={`flex items-center justify-center lg:justify-start gap-3 w-full p-2.5 lg:p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-xs ${adminTab === 'live' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20 shadow-[0_0_15px_rgba(59,130,246,0.1)]' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${adminTab === 'live' ? 'bg-blue-500/20 border-blue-500/30' : 'bg-slate-800 border-white/5'}`}>
                        <Activity size={16} className={adminTab === 'live' ? 'animate-pulse' : ''} />
                      </div>
                      <span className="hidden lg:block text-left">Suivi Trafic Live</span>
                    </button>

                    <button onClick={() => setAdminTab('ltv')} title="ASS" className={`flex items-center justify-center lg:justify-start gap-3 w-full p-2.5 lg:p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-xs ${adminTab === 'ltv' ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 shadow-[0_0_15px_rgba(6,182,212,0.1)]' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${adminTab === 'ltv' ? 'bg-cyan-500/20 border-cyan-500/30' : 'bg-slate-800 border-white/5'}`}>
                        <AlertTriangle size={16} />
                      </div>
                      <span className="hidden lg:block text-left">ASS</span>
                    </button>

                    <button onClick={() => setAdminTab('missions')} title="Plan de Transport (Missions)" className={`flex items-center justify-center lg:justify-start gap-3 w-full p-2.5 lg:p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-xs ${adminTab === 'missions' ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 shadow-[0_0_15px_rgba(6,182,212,0.1)]' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${adminTab === 'missions' ? 'bg-cyan-500/20 border-cyan-500/30' : 'bg-slate-800 border-white/5'}`}>
                        <FileSpreadsheet size={16} />
                      </div>
                      <span className="hidden lg:block text-left">Missions</span>
                    </button>

                    <button onClick={() => setAdminTab('operations')} title="Suivi des Opérations" className={`flex items-center justify-center lg:justify-start gap-3 w-full p-2.5 lg:p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-xs ${adminTab === 'operations' ? 'bg-red-500/10 text-red-500 border border-red-500/20 shadow-[0_0_15px_rgba(239,68,68,0.1)]' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${adminTab === 'operations' ? 'bg-red-500/20 border-red-500/30' : 'bg-slate-800 border-white/5'}`}>
                        <Shield size={16} />
                      </div>
                      <span className="hidden lg:block text-left">Suivi des Opérations</span>
                    </button>

                    <button onClick={() => setAdminTab('security_docs')} title="Documents de Sécurité" className={`flex items-center justify-center lg:justify-start gap-3 w-full p-2.5 lg:p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-xs relative ${adminTab === 'security_docs' ? 'bg-red-500/10 text-red-500 border border-red-500/20 shadow-[0_0_15px_rgba(239,68,68,0.1)]' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${adminTab === 'security_docs' ? 'bg-red-500/20 border-red-500/30' : 'bg-slate-800 border-white/5'}`}>
                        <FileText size={16} />
                      </div>
                      <span className="hidden lg:block text-left">Documents de Sécurité</span>
                      {unreadSecuCount > 0 && (
                        <span className="absolute top-2 right-2 lg:static lg:ml-auto w-2 h-2 bg-red-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(239,68,68,0.5)]" />
                      )}
                    </button>

                    <button onClick={() => setAdminTab('users')} title="Gestion des Agents" className={`flex items-center justify-center lg:justify-start gap-3 w-full p-2.5 lg:p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-xs ${adminTab === 'users' ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 shadow-[0_0_15px_rgba(6,182,212,0.1)]' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${adminTab === 'users' ? 'bg-cyan-500/20 border-cyan-500/30' : 'bg-slate-800 border-white/5'}`}>
                        <Users size={16} />
                      </div>
                      <span className="hidden lg:block text-left">Gestion des Agents</span>
                    </button>

                    <button onClick={() => setAdminTab('planning')} title="Gestion des Plannings" className={`flex items-center justify-center lg:justify-start gap-3 w-full p-2.5 lg:p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-xs ${adminTab === 'planning' ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 shadow-[0_0_15px_rgba(6,182,212,0.1)]' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${adminTab === 'planning' ? 'bg-cyan-500/20 border-cyan-500/30' : 'bg-slate-800 border-white/5'}`}>
                        <FileText size={16} />
                      </div>
                      <span className="hidden lg:block text-left">Gestion Plannings</span>
                    </button>

                    <button onClick={() => setAdminTab('leaves')} title="Congés Conducteurs" className={`flex items-center justify-center lg:justify-start gap-3 w-full p-2.5 lg:p-4 rounded-xl transition-all font-bold uppercase tracking-wide text-xs ${adminTab === 'leaves' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20 shadow-[0_0_15px_rgba(245,158,11,0.1)]' : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent'}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${adminTab === 'leaves' ? 'bg-amber-500/20 border-amber-500/30' : 'bg-slate-800 border-white/5'}`}>
                        <CalendarDays size={16} />
                      </div>
                      <span className="hidden lg:block text-left">Congés</span>
                    </button>
                  </nav>
                </div>

                {/* ADMIN MAIN CONTENT AREA */}
                <div className="flex-1 relative overflow-auto bg-[#0a0f1c]">
                  <div className="min-h-full p-6 sm:p-8">
                    {adminTab === 'ltv' && (
                      <div className="max-w-7xl mx-auto space-y-6">
                        <div className="flex bg-slate-800/60 p-1.5 rounded-2xl gap-1 border border-white/5 max-w-md mb-8 shadow-lg">
                          <button
                            onClick={() => setAdminLtvLine('Centrale')}
                            className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${
                              adminLtvLine === 'Centrale'
                                ? 'bg-cyan-500 text-white shadow-[0_0_20px_rgba(6,182,212,0.4)] border border-cyan-400/50'
                                : 'text-slate-500 hover:text-white'
                            }`}
                          >
                            Ligne Centrale
                          </button>
                          <button
                            onClick={() => setAdminLtvLine('Balagne')}
                            className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${
                              adminLtvLine === 'Balagne'
                                ? 'bg-red-500 text-white shadow-[0_0_20px_rgba(239,68,68,0.4)] border border-red-400/50'
                                : 'text-slate-500 hover:text-white'
                            }`}
                          >
                            Ligne Balagne
                          </button>
                        </div>
                        <SlowDownManager
                          line={adminLtvLine}
                          ltvList={ltvList.filter(ltv =>
                            adminLtvLine === 'Centrale'
                              ? ltv.line === 'Centrale' || !ltv.line
                              : ltv.line === 'Balagne'
                          )}
                          onRefresh={fetchLTVs}
                        />
                      </div>
                    )}
                    
                    {adminTab === 'missions' && (
                      <div className="max-w-7xl mx-auto bg-slate-900/40 rounded-3xl border border-white/5 p-6 backdrop-blur-sm">
                        <MissionManager 
                          missions={missions.filter(m => !m.id.startsWith('permanent-'))} 
                          onAdd={handleAddMission} 
                          onAddMultiple={handleAddMultipleMissions} 
                          onUpdate={handleUpdateMission} 
                          onDelete={handleDeleteMission} 
                        />
                      </div>
                    )}
                    
                    {adminTab === 'users' && <AdminUserManagement />}
                    {adminTab === 'leaves' && <ConductorLeavesManager />}

                    {adminTab === 'planning' && (
                      <div className="max-w-7xl mx-auto space-y-6">
                        <div className="bg-slate-900/40 rounded-3xl border border-white/5 p-6 backdrop-blur-sm">
                          <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20">
                              <FileText className="text-cyan-400" size={24} />
                            </div>
                            <div>
                              <h3 className="text-white font-black uppercase tracking-widest text-base">Gestion des Plannings de Service</h3>
                              <p className="text-slate-400 text-xs mt-0.5 font-bold uppercase tracking-wider">Téléversement et mise à jour en direct des plannings par zone.</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {['Bastia', 'Ajaccio', 'Calvi'].map(zone => {
                              const zoneKey = zone.toLowerCase();
                              const isUploading = !!planningUploading[zoneKey];
                              const error = planningError[zoneKey];
                              const success = planningSuccess[zoneKey];

                              return (
                                <div key={zone} className="bg-slate-800/40 border border-white/5 p-6 rounded-2xl flex flex-col justify-between hover:border-cyan-500/30 transition-all">
                                  <div>
                                    <h4 className="text-white font-bold text-lg mb-2">{zone}</h4>
                                    <p className="text-xs text-slate-500 mb-6">
                                      Glissez-déposez ou sélectionnez le planning PDF officiel de la zone {zone} pour écraser la version en vigueur.
                                    </p>

                                    {/* DRAG AND DROP ZONE */}
                                    <label
                                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                      onDrop={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const file = e.dataTransfer.files?.[0];
                                        if (file) {
                                          handleUploadPlanning(file, zone);
                                        }
                                      }}
                                      className="relative group cursor-pointer block border-2 border-dashed border-white/10 hover:border-cyan-500/50 rounded-xl p-6 text-center transition-all bg-black/20 mb-4"
                                    >
                                      <input
                                        type="file"
                                        accept=".pdf"
                                        onChange={(e) => {
                                          const file = e.target.files?.[0];
                                          if (file) handleUploadPlanning(file, zone);
                                        }}
                                        disabled={isUploading}
                                        className="hidden"
                                      />
                                      <div className="space-y-2">
                                        {isUploading ? (
                                          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin mx-auto" />
                                        ) : (
                                          <Upload className="w-8 h-8 text-slate-600 group-hover:text-cyan-500 transition-all mx-auto" />
                                        )}
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                          {isUploading ? 'Téléversement...' : 'Déposer ou Choisir PDF'}
                                        </p>
                                      </div>
                                    </label>

                                    {error && (
                                      <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-2 text-red-500 text-[9px] font-bold uppercase tracking-widest">
                                        <AlertTriangle size={14} className="shrink-0" />
                                        <span>{error}</span>
                                      </div>
                                    )}

                                    {success && (
                                      <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center gap-2 text-green-400 text-[9px] font-bold uppercase tracking-widest">
                                        <Check size={14} className="shrink-0" />
                                        <span>{success}</span>
                                      </div>
                                    )}
                                  </div>

                                  <div className="border-t border-white/5 pt-4 mt-2">
                                    <a
                                      href={`${API_BASE_URL}/download.php?file=planning_${zoneKey}&token=${session?.access_token || localStorage.getItem('nexus_token')}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[10px] font-black uppercase tracking-widest text-cyan-400 hover:text-cyan-300 flex items-center justify-center gap-1.5 transition-colors"
                                    >
                                      <FileText size={12} /> Consulter le planning actuel
                                    </a>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {adminTab === 'operations' && (
                      <div className="max-w-7xl mx-auto space-y-6">
                        <div className="bg-slate-900/60 rounded-3xl border border-white/5 p-1 sm:p-6 backdrop-blur-md">
                          <SecurityRegisterView />
                        </div>
                      </div>
                    )}

                    {adminTab === 'security_docs' && (
                      <div className="max-w-7xl mx-auto space-y-6">
                        <div className="bg-slate-900/60 rounded-3xl border border-white/5 p-1 sm:p-6 backdrop-blur-md">
                          <BulletinManager />
                        </div>
                      </div>
                    )}

                    {adminTab === 'live' && (
                      <div className="h-[calc(100vh-120px)] w-full rounded-3xl overflow-hidden border border-white/10 shadow-2xl relative">
                        <MapContainer 
                          center={[42.45, 9.15]} 
                          zoom={10} 
                          style={{ height: '100%', width: '100%' }}
                          className="admin-map"
                        >
                         {mapStyle === 'satellite' ? (
                      <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                    ) : (
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                    )}
                          <Polyline positions={pathBalagne} color="#22d3ee" weight={4} opacity={0.4} />
                          <Polyline positions={pathCentrale} color="#a855f7" weight={4} opacity={0.4} />
                          <PKMarkers />
                          <StationsMapLayer />
                          <LTVMapLayer ltvList={ltvList} />
                          <IncidentsLayer incidents={incidents} />
                          <TrainPositionsLayer />
                        </MapContainer>
                        
                        {/* Overlay Legend */}
                        <div className="absolute top-4 right-4 bg-slate-900/80 backdrop-blur-md p-4 rounded-2xl border border-white/10 z-[1000] pointer-events-none">
                          <h3 className="text-[10px] font-black uppercase text-cyan-500 tracking-widest mb-3">Légende Live</h3>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-slate-900 border border-blue-400 rounded-full shadow-[0_0_5px_rgba(59,130,246,0.5)]"></div>
                              <span className="text-[9px] font-bold text-white uppercase uppercase">Trains en circulation</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                              <span className="text-[9px] font-bold text-slate-300 uppercase">Limitations de vitesse</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-1 bg-cyan-400 rounded-full"></div>
                              <span className="text-[9px] font-bold text-slate-300 uppercase">Réseau Ferrer</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}


            {isSettingsOpen && (
              <div className="absolute bottom-[6rem] right-4 w-64 glass-panel-heavy rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden pointer-events-auto border border-white/20 animate-in slide-in-from-bottom-4 fade-in duration-200 z-[3000]">
                <div className="p-3 border-b border-white/5 space-y-2">
                  <button onClick={() => { setIsEvsPageOpen(true); setIsSettingsOpen(false); }} className="w-full flex items-center gap-3 p-4 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 rounded-2xl transition-all active:scale-95">
                    <FileSpreadsheet size={18} /><span className="text-[11px] font-black uppercase tracking-widest mt-0.5">Carnet EVS</span>
                  </button>
                  <button onClick={() => { setIsAccountPageOpen(true); setIsSettingsOpen(false); }} className="w-full flex items-center justify-between p-4 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/20 rounded-2xl transition-all active:scale-95 group">
                    <div className="flex items-center gap-3">
                      <Shield size={18} className="text-cyan-500" />
                      <span className="text-[11px] font-black uppercase tracking-widest mt-0.5">Mon Compte</span>
                    </div>
                    {unreadSecuCount > 0 && (
                      <span className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(239,68,68,0.5)]" />
                    )}
                  </button>
                </div>
                <div className="p-3 space-y-2">
                  <button onClick={() => { if (!audioUnlocked) { unlockAudio(); setAudioEnabled(true); } else { setAudioEnabled(prev => { if (!prev) playActivationBeep(); return !prev; }); } }} className={`w-full flex items-center justify-between p-3.5 rounded-2xl transition-all active:scale-95 ${(!audioUnlocked || !audioEnabled) ? 'bg-red-500/10 text-red-500 border border-red-500/20' : 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'}`}>
                    <span className="text-[10px] font-black uppercase tracking-tight">{!audioUnlocked ? 'Activer Audio' : (audioEnabled ? 'Audio ON' : 'Audio OFF')}</span>
                    {(!audioUnlocked || !audioEnabled) ? <VolumeX size={18} /> : <Volume2 size={18} />}
                  </button>

                  <button
                    onClick={() => {
                      const next = !backgroundEnabled;
                      setBackgroundEnabled(next);
                      if (Capacitor.isNativePlatform()) {
                        if (next) KeepAwake.keepAwake().catch(() => { });
                        else KeepAwake.allowSleep().catch(() => { });
                      }
                    }}
                    className={`w-full flex items-center justify-between p-3.5 rounded-2xl transition-all active:scale-95 ${!backgroundEnabled ? 'bg-slate-800/40 text-slate-500 border border-white/5' : 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20'}`}
                  >
                    <span className="text-[10px] font-black uppercase tracking-tight">Survie Arrière-plan</span>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${!backgroundEnabled ? 'bg-slate-500' : 'bg-indigo-500 animate-pulse'}`} />
                      <div className={`w-8 h-4 rounded-full relative transition-colors ${backgroundEnabled ? 'bg-indigo-500' : 'bg-slate-700'}`}>
                        <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${backgroundEnabled ? 'left-4.5' : 'left-0.5'}`} style={{ left: backgroundEnabled ? '1.1rem' : '0.125rem' }} />
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      console.log("[SYSTEM] Test de notification programmé dans 5s...");
                      setTimeout(() => {
                        sendLocalNotification("TEST RÉVEIL ÉCRAN", "Si vous voyez ceci, les notifications fonctionnent. L'écran devrait s'être allumé.", 999);
                      }, 5000);
                    }}
                    className="w-full flex items-center justify-center p-3.5 rounded-2xl bg-slate-800/60 text-slate-300 border border-white/5 active:scale-95 text-[10px] font-black uppercase tracking-widest"
                  >
                    🚀 Tester le Réveil (Délai 5s)
                  </button>
                </div>
              </div>
            )}

            {/* Common Overlays */}
            {activeVisualAlert && role === 'driver' && (
              <div className="absolute inset-x-0 inset-y-0 z-[2000] bg-red-600/10 flex items-center justify-center pointer-events-none">
                <div className="bg-slate-900 border-8 border-red-600 p-12 rounded-3xl shadow-[0_0_100px_rgba(220,38,38,0.8)] pointer-events-auto text-center">
                  <h2 className="text-4xl font-black uppercase text-red-500 mb-4 animate-pulse">Ralentissement</h2>
                  <div className="bg-red-600 text-white text-[8rem] font-black px-12 py-4 rounded-full border-8 border-slate-900 inline-block mb-4">{activeVisualAlert.speed}</div>
                  <p className="text-2xl font-bold uppercase text-white">{activeVisualAlert.reason}</p>
                </div>
              </div>
            )}

          </main>

          {role === 'driver' && (
            <nav className="h-[calc(6rem+env(safe-area-inset-bottom))] pb-[calc(1rem+env(safe-area-inset-bottom))] shrink-0 bg-[#161f31] flex justify-around items-center z-[2000] border-t border-black/30 w-full relative">
              <button
                onClick={() => { setIsSecuDashboardOpen(true); setIsStopRequestsPageOpen(false); setIsEvsPageOpen(false); setIsPhoneDirectoryOpen(false); setIsSettingsOpen(false); }}
                className={`flex flex-col items-center justify-center w-16 h-16 transition-all gap-1 opacity-60 hover:opacity-100 flex-1 relative ${(!isSecuDashboardOpen && !isEvsPageOpen && !isSettingsOpen) ? 'text-white' : 'text-slate-400'}`}
              >
                <Shield className="w-6 h-6 mb-0.5" strokeWidth={1.5} />
                {unreadSecuCount > 0 && (
                  <span className="absolute top-2 right-1/2 translate-x-4 w-3 h-3 bg-red-500 border-2 border-[#161f31] rounded-full animate-pulse" />
                )}
                <span className="text-[9px] font-black tracking-widest uppercase mt-0.5">SECU</span>
              </button>
              <button
                onClick={() => { setIsStopRequestsPageOpen(false); setIsEvsPageOpen(false); setIsPhoneDirectoryOpen(false); setIsSettingsOpen(false); }}
                className="flex flex-col items-center justify-center w-16 h-16 text-slate-400 hover:text-white transition-all gap-1 opacity-60 hover:opacity-100 flex-1"
              >
                <AlertTriangle className="w-6 h-6 mb-0.5" strokeWidth={1.5} />
                <span className="text-[9px] font-black tracking-widest uppercase mt-0.5">ALERTES</span>
              </button>
              <button
                onClick={() => { console.log("[DEBUG] Opening Stop Requests Detailed View"); setIsStopRequestsPageOpen(true); setIsEvsPageOpen(false); setIsSettingsOpen(false); setIsPhoneDirectoryOpen(false); }}
                className={`flex flex-col items-center justify-center w-16 h-16 transition-all gap-1 flex-1 relative top-[-1px]
                ${requestedStops.some(s => s.trim().toLowerCase() === (displayStatus?.stationName || '').trim().toLowerCase())
                    ? 'text-blue-500 opacity-100 animate-pulse drop-shadow-[0_0_8px_rgba(59,130,246,0.6)]'
                    : 'text-slate-400 opacity-60 hover:opacity-100 hover:text-white'}`}>
                <Hand className={`w-6 h-6 mb-0.5 ${requestedStops.some(s => s.trim().toLowerCase() === (displayStatus?.stationName || '').trim().toLowerCase()) ? 'fill-blue-500/20' : ''}`} strokeWidth={1.5} />
                <span className="text-[8px] font-black tracking-[0.03em] uppercase text-center leading-[1.2] px-1 mt-0.5">ARRÊT<br />DEMANDÉ</span>
              </button>
              <button
                onClick={() => { setIsStopRequestsPageOpen(false); setIsEvsPageOpen(!isEvsPageOpen); setIsSettingsOpen(false); setIsPhoneDirectoryOpen(false); }}
                className={`flex flex-col items-center justify-center w-16 h-16 transition-all gap-1 flex-1 ${isEvsPageOpen ? 'text-[#10b981] opacity-100' : 'text-slate-400 opacity-60 hover:opacity-100 hover:text-white'}`}
              >
                <FileSpreadsheet className="w-6 h-6 mb-0.5" strokeWidth={1.5} />
                <span className="text-[9px] font-black tracking-widest uppercase mt-0.5">EVS</span>
              </button>
              <button
                onClick={() => { setIsStopRequestsPageOpen(false); setIsSettingsOpen(!isSettingsOpen); setIsPhoneDirectoryOpen(false); setIsEvsPageOpen(false); }}
                className={`flex flex-col items-center justify-center w-16 h-16 transition-all gap-1 flex-1 ${isSettingsOpen ? 'text-[#3b82f6] opacity-100' : 'text-slate-400 opacity-60 hover:opacity-100 hover:text-white'}`}
              >
                <Settings className="w-6 h-6 mb-0.5" strokeWidth={1.5} />
                <span className="text-[9px] font-black tracking-widest uppercase mt-0.5">RÉGLAGES</span>
              </button>
            </nav>
          )}

        </div>
      )}

      {updateAvailable && (
        <div className="fixed top-6 left-1/2 transform -translate-x-1/2 z-[15000] w-full max-w-sm px-4 animate-in slide-in-from-top-4 duration-300">
          <button
            onClick={() => {
              if (updateAvailable.targetUrl) {
                window.location.href = updateAvailable.targetUrl;
              } else {
                window.location.reload();
              }
            }}
            className="w-full flex items-center justify-between gap-4 p-4 rounded-2xl bg-slate-900/95 backdrop-blur-md border border-cyan-500/30 shadow-[0_10px_30px_rgba(6,182,212,0.2)] text-left hover:border-cyan-400 hover:shadow-[0_10px_30px_rgba(6,182,212,0.4)] transition-all duration-300 group animate-pulse"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 group-hover:scale-105 transition-transform">
                <RefreshCw size={20} className="animate-spin" style={{ animationDuration: '6s' }} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-xs font-black uppercase tracking-wider text-white truncate">
                  {updateAvailable.type === 'code_update' ? 'Mise à jour installée' : 'Connexion rétablie'}
                </h4>
                <p className="text-[9px] text-slate-400 mt-0.5 leading-relaxed">
                  {updateAvailable.type === 'code_update' 
                    ? "Une nouvelle version est prête. Cliquez pour l'appliquer." 
                    : "Le serveur en ligne est accessible. Cliquez pour vous connecter."}
                </p>
              </div>
            </div>
            <span className="text-[9px] font-black uppercase tracking-widest text-cyan-400 bg-cyan-500/10 px-2.5 py-1 rounded-lg border border-cyan-500/20 group-hover:bg-cyan-500 group-hover:text-slate-900 transition-all shrink-0">
              Recharger
            </span>
          </button>
        </div>
      )}

      {/* Overlays Globaux (accessibles à tous les rôles) */}
      {isStopRequestsPageOpen && activeMission && (
        <RequestedStopsView
          stopRequests={stopRequests}
          activeMission={activeMission}
          onClose={() => setIsStopRequestsPageOpen(false)}
          onAccept={handleAcceptStop}
          onReject={handleRejectStop}
        />
      )}

      {isSecuDashboardOpen && (
        <SecuDashboard
          onClose={() => setIsSecuDashboardOpen(false)}
          driverName={displayName}
          allowedZones={session?.user?.user_metadata?.zones || ''}
        />
      )}

      {activePdf && (
        <PdfReaderView
          url={activePdf.url}
          title={activePdf.title}
          onConfirm={handleConfirmPdfReading}
        />
      )}

      {session && !activeMission && (
        <button
          onClick={() => setIsDownloadModalOpen(true)}
          className="fixed bottom-6 left-6 z-[2000] bg-slate-800/90 hover:bg-slate-700 text-cyan-400 hover:text-white border border-white/10 hover:border-cyan-500/30 p-3.5 rounded-2xl shadow-2xl transition-all active:scale-95 group flex items-center gap-2"
          title="Télécharger les applications"
        >
          <Download size={18} className="group-hover:scale-110 transition-transform" />
          <span className="text-[10px] font-black uppercase tracking-wider hidden sm:inline">Apps</span>
        </button>
      )}

      {isDownloadModalOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-md" onClick={() => setIsDownloadModalOpen(false)} />
          <div className="relative bg-slate-900 border border-white/10 p-6 rounded-3xl shadow-2xl w-full max-w-md animate-in zoom-in-95 duration-200">
            <button
              onClick={() => setIsDownloadModalOpen(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
            <h3 className="text-lg font-black uppercase text-white tracking-widest mb-2 text-center">
              Téléchargements ASTA RAIL
            </h3>
            <p className="text-xs text-slate-400 text-center mb-6">
              Téléchargez les versions officielles pour vos terminaux
            </p>
            <div className="flex flex-col gap-4">
              <a
                href={`${API_BASE_URL}/download.php?file=apk&token=${session?.access_token || localStorage.getItem('nexus_token')}`}
                className="bg-slate-800 hover:bg-slate-700 text-cyan-400 border border-white/5 hover:border-cyan-500/30 text-center py-4 px-4 rounded-2xl text-xs font-bold transition-all flex items-center justify-center gap-3"
              >
                <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.5 10c.8 0 1.5.7 1.5 1.5v4c0 .8-.7 1.5-1.5 1.5s-1.5-.7-1.5-1.5v-4c0-.8.7-1.5 1.5-1.5zm-11 0c.8 0 1.5.7 1.5 1.5v4c0 .8-.7 1.5-1.5 1.5S5 16.3 5 15.5v-4c0-.8.7-1.5 1.5-1.5zm11-4c.8 0 1.5.7 1.5 1.5v1.5H6V7.5C6 6.7 6.7 6 7.5 6h1v-.5c0-.8.7-1.5 1.5-1.5s1.5.7 1.5 1.5V6h1v-.5c0-.8.7-1.5 1.5-1.5s1.5.7 1.5 1.5V6h1zM12 18.5c-3.6 0-6.5-2.9-6.5-6.5h13c0 3.6-2.9 6.5-6.5 6.5z"/>
                </svg>
                <span>Application Android (APK)</span>
              </a>
              <a
                href={`${API_BASE_URL}/download.php?file=exe&token=${session?.access_token || localStorage.getItem('nexus_token')}`}
                className="bg-slate-800 hover:bg-slate-700 text-cyan-400 border border-white/5 hover:border-cyan-500/30 text-center py-4 px-4 rounded-2xl text-xs font-bold transition-all flex items-center justify-center gap-3"
              >
                <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M0 3.449L9.75 2.1v9.45H0V3.449zM0 12.45h9.75v9.45L0 20.551v-8.101zm10.8 9.58L24 24v-11.55H10.8v9.58zM10.8 11.55H24V0l-13.2 1.97v9.58z"/>
                </svg>
                <span>Application Windows (PC)</span>
              </a>
            </div>
            <div className="mt-6 text-center text-slate-500 text-[9px] uppercase tracking-wider">
              Votre jeton de session est inclus pour l'autorisation
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function App() {
  return (
    <TrackProvider>
      <AppContent />
    </TrackProvider>
  );
}
